import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../services/services/APIs';
import { useDashboard } from '../../context/DashboardContext';
import { useAuth } from '../../context/AuthContext';
import ReportesFiltrosCajasEmpresa from '../../components/reportes/ReportesFiltrosCajasEmpresa/ReportesFiltrosCajasEmpresa';
import ReporteFiltrosEncabezadoLista from '../../components/reportes/ReporteFiltrosEncabezadoLista/ReporteFiltrosEncabezadoLista';
import {
  buildCajasAgrupadas,
  buildCajasEncabezado,
  buildReporteCajaParams,
  buildReporteSucursalParams,
  buildSucursalesEncabezado,
  cajasTodasEnScope,
  codigosFromCajaKeys,
  filterCajasCatalogoPorSucursales,
  reporteTieneFiltroCajasSuficiente,
  resolveReporteFiltrosCombinado,
  resolveSucursalesEncabezadoSeleccionadas,
} from '../../utils/reportesCajasEmpresaModel';
import ProductosMeseroReportLayout from '../../components/reportes/ProductosMeseroReportLayout/ProductosMeseroReportLayout';
import PopularidadReportLayout from '../../components/reportes/PopularidadReportLayout/PopularidadReportLayout';
import DescuentosCortesiasReportLayout from '../../components/reportes/DescuentosCortesiasReportLayout/DescuentosCortesiasReportLayout';
import CancelacionesReportLayout from '../../components/reportes/CancelacionesReportLayout/CancelacionesReportLayout';
import GastosReportLayout from '../../components/reportes/GastosReportLayout/GastosReportLayout';
import RelacionVentasReportLayout from '../../components/reportes/RelacionVentasReportLayout/RelacionVentasReportLayout';
import RelacionVentasSoloTotalesLayout from '../../components/reportes/RelacionVentasSoloTotalesLayout/RelacionVentasSoloTotalesLayout';
import VentasPorHoraReportLayout from '../../components/reportes/VentasPorHoraReportLayout/VentasPorHoraReportLayout';
import { downloadProductosMeseroPdf } from '../../utils/productosMeseroPdf';
import { buildProductosMeseroExcelAoa } from '../../utils/productosMeseroExcel';
import { excelSheetName } from '../../utils/reporteTabularExport';
import { downloadPopularidadExcelFile } from '../../utils/popularidadExcel';
import { downloadPopularidadPdf } from '../../utils/popularidadPdf';
import { downloadDescuentosCortesiasPdf } from '../../utils/descuentosCortesiasPdf';
import { downloadDescuentosCortesiasExcelFile } from '../../utils/descuentosCortesiasExcel';
import { downloadCancelacionesPdf } from '../../utils/cancelacionesPdf';
import { downloadCancelacionesExcelFile } from '../../utils/cancelacionesExcel';
import { CANCELACIONES_REPORT_TITULO_DEFAULT } from '../../utils/cancelacionesReportModel';
import { downloadGastosPdf } from '../../utils/gastosPdf';
import { downloadGastosExcelFile } from '../../utils/gastosExcel';
import { GASTOS_REPORT_TITULO_DEFAULT, getTurnoNombreDisplay } from '../../utils/gastosReportModel';
import { downloadRelacionVentasPdf } from '../../utils/relacionVentasPdf';
import { downloadRelacionVentasSoloTotalesPdf } from '../../utils/relacionVentasSoloTotalesPdf';
import { downloadRelacionVentasExcelFile } from '../../utils/relacionVentasExcel';
import { downloadVentasPorHoraPdf } from '../../utils/ventasPorHoraPdf';
import { downloadVentasPorHoraExcelFile } from '../../utils/ventasPorHoraExcel';
import { downloadIngresoPorDiaPdf } from '../../utils/ingresoPorDiaPdf';
import { downloadIngresoPorDiaExcelFile } from '../../utils/ingresoPorDiaExcel';
import IngresoPorDiaReportLayout from '../../components/reportes/IngresoPorDiaReportLayout/IngresoPorDiaReportLayout';
import { extractIngresoPorDiaEncabezado } from '../../utils/ingresoPorDiaReportModel';
import {
  resolveVentasPorHoraColumns,
  sortVentasPorHoraRowsForDisplay,
  VENTAS_POR_HORA_TITULO_DEFAULT,
} from '../../utils/ventasPorHoraReportModel';
import { resolveDescuentosColumns, sortDescuentosRowsForDisplay } from '../../utils/descuentosCortesiasReportModel';
import {
  RELACION_VENTAS_TITULO_DEFAULT,
  resolveRelacionVentasColumns,
  sortRelacionRowsForDisplay,
} from '../../utils/relacionVentasReportModel';
import { shouldOcultarFoliosSoloTotales } from '../../utils/relacionVentasSoloTotalesModel';
import {
  extractEncabezadoFromPopularidadPayload,
  extractPopularidadFooterFromPayload,
  flattenNestedEncabezadoApiShape,
  pickEncabezadoDomicilioLoose,
  pickEncabezadoRfcLoose,
} from '../../utils/popularidadReportModel';
import { isoADisplayDDMMAAAA } from '../../utils/fechaReporte';
import { sortPopularidadRows } from '../../utils/reportesSort';
import { normalizeSucursalesList } from '../../utils/sucursalesList';
import * as XLSX from 'xlsx-js-style';
import './Reportes.css';

const REPORTES = {
  'ingreso-por-dia': {
    title: 'Ingresos',
    endpoint: 'ingresos',
    fetcher: api.reportes.ingresoPorDia,
  },
  popularidad: {
    title: 'Popularidad de productos',
    endpoint: 'popularidad',
    fetcher: api.reportes.popularidad,
  },
  'descuentos-cortesias': {
    title: 'Relación de descuentos y cortesías',
    endpoint: 'descuentos-cortesias',
    fetcher: api.reportes.descuentosCortesias,
  },
  cancelaciones: {
    title: 'Cancelaciones',
    endpoint: 'cancelaciones',
    fetcher: api.reportes.cancelaciones,
  },
  gastos: {
    title: 'Gastos',
    endpoint: 'gastos',
    fetcher: api.reportes.gastos,
  },
  'relacion-ventas': {
    title: 'Relacion de ventas',
    endpoint: 'relacion-ventas',
    fetcher: api.reportes.relacionVentas,
  },
  'ventas-por-hora': {
    title: 'Ventas por hora',
    endpoint: 'ventas-por-hora',
    fetcher: api.reportes.ventasPorHora,
  },
  'productos-vendidos-por-mesero': {
    title: 'Productos vendidos por mesero',
    endpoint: 'report-products-sold-by-waiter',
    fetcher: api.reportes.productosVendidosPorMesero,
  },
};

/** Evita que "Excel" / mayúsculas o espacios disparen export al servidor. */
const normalizeExportFormat = (formato) =>
  String(formato ?? '')
    .trim()
    .toLowerCase();

/**
 * En esta pantalla, PDF y Excel de todos estos reportes se arman en el navegador.
 * No debe llamarse `reportes/.../export` ni `cancelaciones/export` para pdf/xlsx.
 */
const EXPORT_PDF_EXCEL_SIN_API = new Set(Object.values(REPORTES).map((r) => r.endpoint));

const today = () => new Date().toISOString().slice(0, 10);

const formatCell = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number') return value.toLocaleString('es-MX', { maximumFractionDigits: 2 });
  return String(value);
};

/** Valor mostrado en inputs numéricos que permiten quedar vacíos al editar. */
const displayNumericField = (value) =>
  value === '' || value === null || value === undefined ? '' : String(value);

/** Parsea input numérico; '' vacío, undefined = ignorar cambio inválido. */
const parseNumericFieldInput = (raw) => {
  const trimmed = String(raw ?? '').trim();
  if (trimmed === '') return '';
  if (!/^\d+$/.test(trimmed)) return undefined;
  return Number(trimmed);
};

const toOptionalPositiveNumber = (value) => {
  if (value === '' || value === null || value === undefined) return NaN;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
};

/** Al subir el valor inicial, el final no puede quedar por debajo. */
const applyGastosRangoInicio = (inicioNext, finCurrent) => {
  if (inicioNext === '' || typeof inicioNext !== 'number') {
    return { inicio: inicioNext, fin: finCurrent };
  }
  const fin = toOptionalPositiveNumber(finCurrent);
  if (Number.isFinite(fin) && fin < inicioNext) {
    return { inicio: inicioNext, fin: inicioNext };
  }
  return { inicio: inicioNext, fin: finCurrent };
};

/** El valor final debe ser >= al inicial (si ambos están definidos). */
const acceptGastosRangoFin = (inicioCurrent, finNext) => {
  if (finNext === '' || typeof finNext !== 'number') return true;
  const ini = toOptionalPositiveNumber(inicioCurrent);
  if (!Number.isFinite(ini)) return true;
  return finNext >= ini;
};

const DEFAULTS = {
  fechaInicio: today(),
  fechaFin: today(),
  cajaInicio: null,
  cajaFin: null,
  sucursalesTodas: true,
  sucursalesSeleccionadas: [],
  cajasTodas: true,
  cajasSeleccionadas: [],
  turnoInicio: 1,
  turnoFin: 3,
  ordenarMesa: false,
  soloCortesias: false,
  incluirCortesias: true,
  bebidasAlcoholicas: false,
  incluirAlimentos: true,
  incluirBebidas: true,
  incluirOtros: true,
  incluirPrecioMenu: true,
  porFamilia: true,
  mayorIngreso: false,
  mayorVenta: false,
  incluirEstadisticas: false,
  agruparTurno: false,
  soloTotales: false,
  sucursalId: '',
  meseroId: '',
  groupBy: '',
  vistaIngreso: 'dia',
  soloUsanAutorizacion: false,
  mostrarDescripcion: false,
  global: true,
  gastosGlobal: false,
  tipoGasto: '',
  ordenMeseros: 'ventas-desc',
  noGlobalAsterisco: false,
};

const MESERO_HEADER_FIJO = {
  nombreComercial: 'MUNDO MAYA CALAKMUL',
  razonSocial:
    'GRUPO AEROPORTUARIO, FERROVIARIO, DE SERVICIOS AUXILIARES Y CONEXOS, OLMECA-MAYA-MEXICA S.A. DE C.V.',
  domicilioFiscal:
    'Carr. Campeche Conhuas- Calakmul km 49, Hormiguero C.p.24640 CALAKMUL, CAMPECHE',
  rfc: 'GAF220603TC4',
  reporteTitulo: 'REPORTE PRODUCTOS VENDIDOS INCLUYENDO CORTESIAS',
};

/** `data` del JSON o, si el API envía el paquete en raíz (`filas`, `encabezado`, etc.), ese objeto. */
const parseJsonData = (json) => {
  if (!json?.success) return [];
  const d = json.data;
  if (Array.isArray(d)) return d;
  if (d != null && typeof d === 'object') return d;
  if (Array.isArray(json.filas)) return json;
  return [];
};

/** El API puede devolver el listado en `data` como arreglo o anidado (filas, rows, items, etc.). */
const coerceReportRows = (data) => {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (typeof data !== 'object') return [];
  const keys = [
    'filas',
    'rows',
    'items',
    'detalle',
    'detalles',
    'resultados',
    'registros',
    'lineas',
    'listado',
    'data',
  ];
  for (const k of keys) {
    const v = data[k];
    if (Array.isArray(v)) return v;
  }
  return [];
};

/** Filas del reporte popularidad: mismo criterio que el resto de reportes (`data`, objeto anidado o `filas` en raíz). */
const extractPopularidadRowsFromJson = (json) => {
  if (!json?.success) return [];
  const data = json.data;
  if (Array.isArray(data)) return data;
  if (data != null && typeof data === 'object') return coerceReportRows(data);
  return coerceReportRows(parseJsonData(json));
};

export default function Reportes() {
  const { tipo } = useParams();
  const { user } = useAuth();
  const { selectedSucursalId, sucursales, setSucursales } = useDashboard();
  const reporte = REPORTES[tipo];

  const [form, setForm] = useState({
    ...DEFAULTS,
    sucursalId: '',
  });
  const [cajasCatalogoList, setCajasCatalogoList] = useState([]);
  const [cajasCatalogoLoading, setCajasCatalogoLoading] = useState(false);

  const empresaNombre = useMemo(() => {
    return (
      user?.nombreEmpresa ||
      user?.Empresa?.nombre ||
      ''
    ).replace(/^Empresa\s+/i, '');
  }, [user]);

  const reporteFiltros = useMemo(
    () =>
      resolveReporteFiltrosCombinado({
        sucursalesTodas: form.sucursalesTodas,
        sucursalesSeleccionadas: form.sucursalesSeleccionadas,
        cajasTodas: form.cajasTodas,
        cajasSeleccionadas: form.cajasSeleccionadas,
        sucursales,
        cajasCatalogo: cajasCatalogoList,
      }),
    [
      form.sucursalesTodas,
      form.sucursalesSeleccionadas,
      form.cajasTodas,
      form.cajasSeleccionadas,
      sucursales,
      cajasCatalogoList,
    ],
  );

  const sucursalFiltro = reporteFiltros.sucursal;
  const cajaFiltro = reporteFiltros.caja;

  const cajasTodasEfectivas = cajasTodasEnScope(
    form.cajasTodas,
    form.sucursalesTodas,
    form.sucursalesSeleccionadas,
  );

  const permitirCajasTodas =
    form.sucursalesTodas || form.sucursalesSeleccionadas.length > 0;

  const sucursalesEncabezadoSeleccionadas = useMemo(
    () =>
      resolveSucursalesEncabezadoSeleccionadas({
        sucursalesTodas: form.sucursalesTodas,
        sucursalesSeleccionadas: form.sucursalesSeleccionadas,
        cajasTodas: form.cajasTodas,
        cajasSeleccionadas: form.cajasSeleccionadas,
        sucursales,
        cajasCatalogo: cajasCatalogoList,
      }),
    [
      form.sucursalesTodas,
      form.sucursalesSeleccionadas,
      form.cajasTodas,
      form.cajasSeleccionadas,
      sucursales,
      cajasCatalogoList,
    ],
  );

  const selectedSuc = useMemo(() => {
    if (sucursalFiltro.allBranches || !sucursalFiltro.sucursalId) return null;
    return sucursales.find((s) => String(s.sucursalId) === String(sucursalFiltro.sucursalId));
  }, [sucursales, sucursalFiltro]);

  const gastosTodasSucursales = form.sucursalesTodas || sucursalFiltro.allBranches;

  const gastosTurnoRangoInvalido = useMemo(() => {
    const ini = toOptionalPositiveNumber(form.turnoInicio);
    const fin = toOptionalPositiveNumber(form.turnoFin);
    return Number.isFinite(ini) && Number.isFinite(fin) && fin < ini;
  }, [form.turnoInicio, form.turnoFin]);

  const [rows, setRows] = useState([]);
  const [encabezadoMesero, setEncabezadoMesero] = useState(null);
  const [encabezadoPopularidad, setEncabezadoPopularidad] = useState(null);
  const [encabezadoDescuentos, setEncabezadoDescuentos] = useState(null);
  /** Pie de tabla / etiquetas opcionales del API (`totales`, `columnas`). */
  const [descuentosApiBundle, setDescuentosApiBundle] = useState(null);
  const [encabezadoCancelaciones, setEncabezadoCancelaciones] = useState(null);
  const [cancelacionesApiBundle, setCancelacionesApiBundle] = useState(null);
  const [encabezadoGastos, setEncabezadoGastos] = useState(null);
  const [gastosApiBundle, setGastosApiBundle] = useState(null);
  const [gastosCatalogo, setGastosCatalogo] = useState({
    cajaInicioNombre: '',
    cajaFinNombre: '',
    cajaInicioValida: true,
    cajaFinValida: true,
    turnoInicioNombre: '',
    turnoFinalNombre: '',
  });

  const filtrosEncabezado = useMemo(
    () => ({
      sucursales: buildSucursalesEncabezado({
        sucursalesTodas: form.sucursalesTodas,
        sucursalesSeleccionadas: sucursalesEncabezadoSeleccionadas,
        sucursales,
      }),
      cajas: buildCajasEncabezado({
        cajasTodas: cajasTodasEfectivas,
        cajasSeleccionadas: form.cajasSeleccionadas,
        cajasCatalogo: cajasCatalogoList,
        sucursales,
        sucursalesTodas: form.sucursalesTodas,
        sucursalesSeleccionadas: form.sucursalesSeleccionadas,
      }),
    }),
    [
      form.sucursalesTodas,
      form.sucursalesSeleccionadas,
      form.cajasSeleccionadas,
      cajasTodasEfectivas,
      sucursalesEncabezadoSeleccionadas,
      sucursales,
      cajasCatalogoList,
    ],
  );

  const syncCajasConSucursales = useCallback(
    (prev) => {
      const filtrado = filterCajasCatalogoPorSucursales(cajasCatalogoList, {
        sucursalesTodas: prev.sucursalesTodas,
        sucursalesSeleccionadas: prev.sucursalesSeleccionadas,
        sucursales,
      });
      const items = buildCajasAgrupadas(cajasCatalogoList, filtrado, {
        sucursalesTodas: prev.sucursalesTodas,
        sucursalesSeleccionadas: prev.sucursalesSeleccionadas,
      });
      const validKeys = new Set(items.map((c) => c.key));
      const prevKeys = (prev.cajasSeleccionadas ?? []).map(String);
      const selected = prevKeys.filter((k) => validKeys.has(k));
      const codigos = codigosFromCajaKeys(selected);
      if (prev.cajasTodas || selected.length === 0) {
        return {
          ...prev,
          cajasSeleccionadas: selected,
          cajaInicio: prev.cajasTodas ? null : codigos[0] ?? null,
          cajaFin: prev.cajasTodas ? null : codigos[codigos.length - 1] ?? null,
        };
      }
      return {
        ...prev,
        cajasSeleccionadas: selected,
        cajaInicio: codigos[0] ?? null,
        cajaFin: codigos[codigos.length - 1] ?? null,
      };
    },
    [cajasCatalogoList, sucursales],
  );

  const [encabezadoRelacionVentas, setEncabezadoRelacionVentas] = useState(null);
  const [relacionVentasApiBundle, setRelacionVentasApiBundle] = useState(null);
  const [relacionVentasSoloTotales, setRelacionVentasSoloTotales] = useState(null);
  const [encabezadoVentasPorHora, setEncabezadoVentasPorHora] = useState(null);
  const [ventasPorHoraApiBundle, setVentasPorHoraApiBundle] = useState(null);
  const [encabezadoIngresoPorDia, setEncabezadoIngresoPorDia] = useState(null);
  const [ingresoPorDiaApiBundle, setIngresoPorDiaApiBundle] = useState(null);
  const [popularidadFooter, setPopularidadFooter] = useState({});
  /** PDF/Excel que repiden el API antes de exportar (todos los reportes con tabla en esta pantalla). */
  const [tablaReporteExportBusy, setTablaReporteExportBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  /** Firma de filtros con la que se generó el reporte visible (evita mezclar datos viejos con filtros nuevos). */
  const [appliedFiltersSignature, setAppliedFiltersSignature] = useState('');
  const loadReportRequestRef = useRef(0);

  const soloTotalesOcultarFolios = useMemo(
    () =>
      shouldOcultarFoliosSoloTotales({
        sucursalesItems: filtrosEncabezado.sucursales.items,
        sucursalesSeleccionadas: form.sucursalesSeleccionadas,
        sucursalesTodas: form.sucursalesTodas,
        sucursales,
        ocultarFoliosApi: relacionVentasSoloTotales?.filtros?.ocultarFolios === true,
      }),
    [
      filtrosEncabezado.sucursales.items,
      form.sucursalesSeleccionadas,
      form.sucursalesTodas,
      sucursales,
      relacionVentasSoloTotales?.filtros?.ocultarFolios,
    ],
  );

  const sucursalesSeleccionadasKey = form.sucursalesSeleccionadas.join('|');

  /**
   * Mantiene `sucursalId` alineado con la selección de empresa cuando hay una sola sucursal.
   * Si "Todas" está activo, no fuerza una sucursal concreta.
   */
  useEffect(() => {
    if (sucursales.length === 0) return;
    setForm((prev) => {
      if (prev.sucursalesTodas) {
        return prev.sucursalId === '' ? prev : { ...prev, sucursalId: '' };
      }
      if (prev.sucursalesSeleccionadas.length === 1) {
        const id = String(prev.sucursalesSeleccionadas[0]);
        return prev.sucursalId === id ? prev : { ...prev, sucursalId: id };
      }
      return prev.sucursalId === '' ? prev : { ...prev, sucursalId: '' };
    });
  }, [sucursales, form.sucursalesTodas, sucursalesSeleccionadasKey]);

  /** Listado de sucursales según alcance de la cuenta (no todas las de la empresa si es subusuario). */
  useEffect(() => {
    let cancelled = false;
    if (!user) return undefined;
    const empresaId = String(
      user.empresaId ??
        user.EmpresaID ??
        user.empresa?.empresaId ??
        user.empresa?.EmpresaID ??
        '',
    ).trim();
    if (!empresaId) return undefined;

    (async () => {
      try {
        const res = await api.getSucursales.byEmpresa(empresaId);
        if (cancelled || !res.ok) return;
        const json = await res.json();
        if (cancelled) return;
        const list = normalizeSucursalesList(json);
        setSucursales(list);
      } catch {
        if (!cancelled) setSucursales([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, user?.personaId, user?.empresaId, setSucursales]);

  /** Defaults al abrir reporte de gastos. */
  useEffect(() => {
    if (tipo !== 'gastos') return;
    setForm((prev) => ({
      ...prev,
      turnoFin: prev.turnoFin ?? prev.turnoInicio ?? 1,
      gastosGlobal: prev.gastosGlobal ?? false,
    }));
  }, [tipo]);

  /** Catálogo de cajas (una sola petición; el filtro por sucursal es en cliente). */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCajasCatalogoLoading(true);
      try {
        const res = await api.reportes.cajasCatalogo({ allBranches: true });
        if (cancelled) return;
        const json = res.ok ? await res.json() : null;
        const list = json?.success && Array.isArray(json.data) ? json.data : [];
        if (!cancelled) setCajasCatalogoList(list);
      } catch {
        if (!cancelled) setCajasCatalogoList([]);
      } finally {
        if (!cancelled) setCajasCatalogoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Re-sincroniza selección de cajas cuando llega el catálogo o cambian sucursales. */
  useEffect(() => {
    if (!cajasCatalogoList.length) return;
    setForm((prev) => {
      const next = syncCajasConSucursales(prev);
      const sameKeys =
        JSON.stringify(next.cajasSeleccionadas) === JSON.stringify(prev.cajasSeleccionadas);
      const sameRange = next.cajaInicio === prev.cajaInicio && next.cajaFin === prev.cajaFin;
      return sameKeys && sameRange ? prev : next;
    });
  }, [cajasCatalogoList, sucursales, syncCajasConSucursales]);

  const onSucursalesTodasChange = (checked) => {
    setForm((prev) => {
      const next = {
        ...prev,
        sucursalesTodas: checked,
        sucursalesSeleccionadas: checked ? [] : prev.sucursalesSeleccionadas,
        sucursalId: checked ? '' : prev.sucursalId,
      };
      return syncCajasConSucursales(next);
    });
  };

  const onSucursalCheckedChange = (id, checked) => {
    setForm((prev) => {
      const set = new Set((prev.sucursalesSeleccionadas ?? []).map(String));
      if (checked) set.add(String(id));
      else set.delete(String(id));
      const arr = [...set];
      const next = {
        ...prev,
        sucursalesTodas: false,
        sucursalesSeleccionadas: arr,
        sucursalId: arr.length === 1 ? arr[0] : '',
      };
      return syncCajasConSucursales(next);
    });
  };

  const onCajasTodasChange = (checked) => {
    setForm((prev) => ({
      ...prev,
      cajasTodas: checked,
      cajasSeleccionadas: [],
      cajaInicio: null,
      cajaFin: null,
    }));
  };

  const onCajaCheckedChange = (cajaKey, checked) => {
    const key = String(cajaKey ?? '').trim();
    if (!key) return;
    setForm((prev) => {
      const set = new Set((prev.cajasSeleccionadas ?? []).map(String));
      if (checked) set.add(key);
      else set.delete(key);
      const arr = [...set];
      const codigos = codigosFromCajaKeys(arr);
      return {
        ...prev,
        cajasTodas: false,
        cajasSeleccionadas: arr,
        cajaInicio: codigos.length ? codigos[0] : null,
        cajaFin: codigos.length ? codigos[codigos.length - 1] : null,
      };
    });
  };

  /** Nombres de caja/turno (formulario legacy). */
  useEffect(() => {
    if (tipo !== 'gastos') return undefined;
    let cancelled = false;
    const cajaIni = toOptionalPositiveNumber(cajaFiltro.cajaInicio);
    const cajaFin = toOptionalPositiveNumber(cajaFiltro.cajaFin);
    const iniOk = Number.isFinite(cajaIni) && cajaIni > 0;
    const finOk = Number.isFinite(cajaFin) && cajaFin > 0;
    const hasCajaLista = (cajaFiltro.cajasCodigos ?? []).length > 0;
    if (!iniOk && !finOk && !hasCajaLista) {
      setGastosCatalogo({
        cajaInicioNombre: '',
        cajaFinNombre: '',
        cajaInicioValida: false,
        cajaFinValida: false,
        turnoInicioNombre: getTurnoNombreDisplay(form.turnoInicio),
        turnoFinalNombre: getTurnoNombreDisplay(form.turnoFin),
      });
      return undefined;
    }
    (async () => {
      try {
        const res = await api.reportes.gastosFiltrosAux({
          ...buildReporteSucursalParams(sucursalFiltro),
          ...buildReporteCajaParams(cajaFiltro),
          turnoInicio: form.turnoInicio,
          turnoFin: form.turnoFin,
        });
        if (cancelled) return;
        const data = res.ok ? await res.json() : null;
        const cat = data?.success ? data.data : null;
        const cajaIniFallback = gastosTodasSucursales && iniOk ? `Caja ${cajaIni}` : '';
        const cajaFinFallback = gastosTodasSucursales && finOk ? `Caja ${cajaFin}` : '';
        setGastosCatalogo({
          cajaInicioNombre: cat?.cajaInicioNombre ?? cajaIniFallback,
          cajaFinNombre: cat?.cajaFinNombre ?? cajaFinFallback,
          cajaInicioValida: iniOk
            ? gastosTodasSucursales
              ? cat?.cajaInicioValida !== false || cajaIni > 0
              : cat?.cajaInicioValida !== false
            : false,
          cajaFinValida: finOk
            ? gastosTodasSucursales
              ? cat?.cajaFinValida !== false || cajaFin > 0
              : cat?.cajaFinValida !== false
            : false,
          turnoInicioNombre:
            cat?.turnoInicioNombre ?? getTurnoNombreDisplay(form.turnoInicio),
          turnoFinalNombre: cat?.turnoFinalNombre ?? getTurnoNombreDisplay(form.turnoFin),
        });
      } catch {
        if (!cancelled) {
          setGastosCatalogo({
            cajaInicioNombre: '',
            cajaFinNombre: '',
            cajaInicioValida: false,
            cajaFinValida: false,
            turnoInicioNombre: getTurnoNombreDisplay(form.turnoInicio),
            turnoFinalNombre: getTurnoNombreDisplay(form.turnoFin),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tipo, cajaFiltro, form.turnoInicio, form.turnoFin, sucursalFiltro, gastosTodasSucursales]);

  const filters = useMemo(() => {
    const cajaParams = buildReporteCajaParams(cajaFiltro);
    const sucursalParams = buildReporteSucursalParams(sucursalFiltro);

    const base = {
      fechaInicio: form.fechaInicio,
      fechaFin: form.fechaFin,
      ...cajaParams,
      turnoInicio: form.turnoInicio,
      turnoFin: form.turnoFin,
      ordenarMesa: form.ordenarMesa,
      ...sucursalParams,
    };

    if (tipo === 'popularidad') {
      return {
        ...base,
        soloCortesias: form.soloCortesias,
        incluirCortesias: form.incluirCortesias,
        bebidasAlcoholicas: form.bebidasAlcoholicas,
        incluirAlimentos: form.incluirAlimentos,
        incluirBebidas: form.incluirBebidas,
        incluirOtros: form.incluirOtros,
        incluirPrecioMenu: form.incluirPrecioMenu,
        porFamilia: form.porFamilia,
        mayorIngreso: form.mayorIngreso,
        mayorVenta: form.mayorVenta,
      };
    }
    if (tipo === 'cancelaciones') {
      return { ...base, incluirEstadisticas: form.incluirEstadisticas };
    }
    if (tipo === 'gastos') {
      return {
        ...base,
        global: form.gastosGlobal,
        ...(form.tipoGasto ? { tipoGasto: form.tipoGasto } : {}),
      };
    }
    if (tipo === 'relacion-ventas') {
      return {
        ...base,
        agruparTurno: form.agruparTurno,
        soloTotales: form.soloTotales,
      };
    }
    if (tipo === 'ingreso-por-dia') {
      const companyId = String(
        user?.empresaId ??
        user?.empresa?.empresaId ??
        ''
      ).trim();
      return {
        ...(companyId ? { companyId } : {}),
        fechaInicio: form.fechaInicio,
        fechaFin: form.fechaFin,
        ...buildReporteCajaParams(cajaFiltro),
        turnoInicio: String(form.turnoInicio ?? ''),
        turnoFin: String(form.turnoFin ?? ''),
        ...buildReporteSucursalParams(sucursalFiltro),
      };
    }
    if (tipo === 'productos-vendidos-por-mesero') {
      const meseroTrim = String(form.meseroId ?? '').trim();
      return {
        ...base,
        ...(meseroTrim !== '' ? { meseroId: meseroTrim } : {}),
        soloCortesias: form.soloCortesias,
        incluirCortesias: form.incluirCortesias,
        soloUsanAutorizacion: form.soloUsanAutorizacion,
        incluirAlimentos: form.incluirAlimentos,
        incluirBebidas: form.incluirBebidas,
        incluirOtros: form.incluirOtros,
        incluirPrecioMenu: form.incluirPrecioMenu,
        mostrarDescripcion: form.mostrarDescripcion,
        porFamilia: form.porFamilia,
        /** Preferencia del usuario; la vista compacta por familias también se arma en el cliente. */
        global: form.global,
        mayorIngreso: form.mayorIngreso,
        mayorVenta: form.mayorVenta,
      };
    }
    return base;
  }, [form, tipo, user?.empresaId, user?.empresa?.empresaId, sucursalFiltro, cajaFiltro]);

  const filtersSignature = useMemo(() => JSON.stringify(filters), [filters]);

  const reporteDesactualizado = Boolean(
    appliedFiltersSignature && filtersSignature !== appliedFiltersSignature && rows.length > 0,
  );

  const descuentosResolvedForSort = useMemo(() => {
    if (tipo !== 'descuentos-cortesias') return null;
    return resolveDescuentosColumns(rows[0] ?? null);
  }, [tipo, rows]);

  const relacionResolvedForSort = useMemo(() => {
    if (tipo !== 'relacion-ventas') return null;
    return resolveRelacionVentasColumns(rows[0] ?? null);
  }, [tipo, rows]);

  const ventasPorHoraResolvedForSort = useMemo(() => {
    if (tipo !== 'ventas-por-hora') return null;
    return resolveVentasPorHoraColumns(rows[0] ?? null);
  }, [tipo, rows]);

  const tablaRows = useMemo(() => {
    if (tipo === 'popularidad') {
      const h = rows.length > 0 ? Object.keys(rows[0]) : [];
      return sortPopularidadRows(rows, h, form.mayorIngreso, form.mayorVenta);
    }
    if (tipo === 'descuentos-cortesias' && descuentosResolvedForSort) {
      return sortDescuentosRowsForDisplay(rows, descuentosResolvedForSort, form.ordenarMesa);
    }
    if (tipo === 'relacion-ventas' && relacionResolvedForSort) {
      return sortRelacionRowsForDisplay(rows, relacionResolvedForSort, form.ordenarMesa);
    }
    if (tipo === 'ventas-por-hora' && ventasPorHoraResolvedForSort) {
      return sortVentasPorHoraRowsForDisplay(rows, ventasPorHoraResolvedForSort);
    }
    return rows;
  }, [
    tipo,
    rows,
    form.mayorIngreso,
    form.mayorVenta,
    form.ordenarMesa,
    descuentosResolvedForSort,
    relacionResolvedForSort,
    ventasPorHoraResolvedForSort,
  ]);

  const popularidadEncabezadoFallback = useMemo(() => {
    const sid = sucursalFiltro.sucursalId || selectedSucursalId;
    const suc = sid ? sucursales.find((s) => String(s.sucursalId) === String(sid)) : null;
    const domSuc =
      pickEncabezadoDomicilioLoose(suc || {}) ||
      suc?.domicilioFiscal?.trim?.() ||
      suc?.domicilio?.trim?.() ||
      suc?.direccion?.trim?.() ||
      '';
    const rfcSuc =
      pickEncabezadoRfcLoose(suc || {}) ||
      suc?.rfc?.trim?.() ||
      suc?.RFC?.trim?.() ||
      '';
    const filterParts = [];
    if (form.soloCortesias) filterParts.push('SOLO CORTESÍAS');
    if (form.bebidasAlcoholicas) filterParts.push('BEBIDAS ALCOHÓLICAS');
    if (form.agruparTurno) filterParts.push('AGRUPADO POR TURNO');
    if (form.ordenarMesa) {
      filterParts.push(tipo === 'relacion-ventas' ? 'ORDEN POR MESA' : 'ORDENADO POR MESA');
    }
    if (
      form.global &&
      tipo === 'productos-vendidos-por-mesero' &&
      form.porFamilia &&
      !form.mayorIngreso &&
      !form.mayorVenta
    ) {
      filterParts.push('MODO GLOBAL');
    }
    if (form.incluirEstadisticas) filterParts.push('CON ESTADÍSTICAS');

    const contents = [];
    if (form.incluirAlimentos) contents.push('ALIMENTOS');
    if (form.incluirBebidas) contents.push('BEBIDAS');
    if (form.incluirOtros) contents.push('OTROS');
    if (contents.length > 0 && contents.length < 3) {
      filterParts.push(`INCLUYE: ${contents.join(', ')}`);
    }
    const filtrosTexto = filterParts.join(' · ');

    return {
      nombreComercial:
        filtrosEncabezado.sucursales.items.length === 1
          ? `${empresaNombre} - ${filtrosEncabezado.sucursales.items[0]}`
          : empresaNombre || suc?.nombreComercial?.trim?.() || '',
      razonSocial: '',
      domicilioFiscal:
        domSuc ||
        pickEncabezadoDomicilioLoose(user || {}) ||
        user?.domicilioFiscal?.trim() ||
        user?.domicilio?.trim() ||
        user?.direccion?.trim() ||
        '',
      responsable: '',
      rfc:
        rfcSuc ||
        pickEncabezadoRfcLoose(user || {}) ||
        user?.rfc?.trim() ||
        '',
      filtrosTexto,
      sucursalesTexto: filtrosEncabezado.sucursales.texto,
      sucursalesItems: filtrosEncabezado.sucursales.items,
      cajasTexto: filtrosEncabezado.cajas.texto,
      cajasItems: filtrosEncabezado.cajas.items,
    };
  }, [user, form, selectedSucursalId, sucursales, empresaNombre, tipo, sucursalFiltro, filtrosEncabezado]);

  const meseroEsModoFamilias = () =>
    form.porFamilia && !form.mayorIngreso && !form.mayorVenta;

  /** Global + familias: solo totales por familia, sin filas de productos/menú. */
  const meseroGlobalSoloTotalesFamilia =
    tipo === 'productos-vendidos-por-mesero' && meseroEsModoFamilias() && form.global;

  /** Aplica el JSON de popularidad al estado (misma lógica que {@link loadReport}). */
  const handlePopularidadJson = (json) => {
    const list = extractPopularidadRowsFromJson(json);
    const data = json?.success ? json.data : null;
    const footer =
      json?.success
        ? {
            ...extractPopularidadFooterFromPayload(json),
            ...extractPopularidadFooterFromPayload(data),
          }
        : {};
    const encP = extractEncabezadoFromPopularidadPayload(json, data);
    const encabezado =
      json?.success && encP ? flattenNestedEncabezadoApiShape(encP) : null;
    setRows(list);
    setPopularidadFooter(footer);
    setEncabezadoPopularidad(encabezado);
    setEncabezadoDescuentos(null);
    setDescuentosApiBundle(null);
    setEncabezadoCancelaciones(null);
    setCancelacionesApiBundle(null);
    setEncabezadoRelacionVentas(null);
    setRelacionVentasApiBundle(null);
    setRelacionVentasSoloTotales(null);
    setEncabezadoVentasPorHora(null);
    setVentasPorHoraApiBundle(null);
    setEncabezadoIngresoPorDia(null);
    setIngresoPorDiaApiBundle(null);
    return { list, footer, encabezado };
  };

  /** Vuelve a pedir popularidad con `filters` actuales y devuelve filas ordenadas para exportar. */
  const refrescarPopularidadParaExport = async () => {
    if (!reporte) throw new Error('Reporte no disponible');
    const res = await reporte.fetcher(filters);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const { list, footer, encabezado } = handlePopularidadJson(json);
    const h = list.length > 0 ? Object.keys(list[0]) : [];
    const rowsForExport = sortPopularidadRows(list, h, form.mayorIngreso, form.mayorVenta);
    return { rowsForExport, footer, encabezado };
  };

  const handleDescuentosCortesiasJson = (json) => {
    const data = json?.success ? json.data : null;
    setPopularidadFooter({});
    setEncabezadoPopularidad(null);
    const parsed = parseJsonData(json);
    const list = coerceReportRows(parsed);
    setRows(list);
    const bundle =
      parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    const apiBundle = bundle
      ? { totales: bundle.totales ?? null, columnas: bundle.columnas ?? null }
      : null;
    setDescuentosApiBundle(apiBundle);
    const encD = extractEncabezadoFromPopularidadPayload(json, bundle ?? data);
    const encabezado =
      json?.success && encD ? flattenNestedEncabezadoApiShape(encD) : null;
    setEncabezadoDescuentos(encabezado);
    setEncabezadoCancelaciones(null);
    setCancelacionesApiBundle(null);
    setEncabezadoRelacionVentas(null);
    setRelacionVentasApiBundle(null);
    setRelacionVentasSoloTotales(null);
    setEncabezadoVentasPorHora(null);
    setVentasPorHoraApiBundle(null);
    setEncabezadoIngresoPorDia(null);
    setIngresoPorDiaApiBundle(null);
    return { list, apiBundle, encabezado };
  };

  const handleCancelacionesJson = (json) => {
    const data = json?.success ? json.data : null;
    setPopularidadFooter({});
    setEncabezadoPopularidad(null);
    const parsed = parseJsonData(json);
    const list = coerceReportRows(parsed);
    setRows(list);
    setDescuentosApiBundle(null);
    setEncabezadoDescuentos(null);
    setEncabezadoRelacionVentas(null);
    setRelacionVentasApiBundle(null);
    setRelacionVentasSoloTotales(null);
    setEncabezadoVentasPorHora(null);
    setVentasPorHoraApiBundle(null);
    setEncabezadoIngresoPorDia(null);
    setIngresoPorDiaApiBundle(null);
    const bundle =
      parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    const apiBundle = bundle
      ? { totales: bundle.totales ?? null, columnas: bundle.columnas ?? null }
      : null;
    setCancelacionesApiBundle(apiBundle);
    const encC = extractEncabezadoFromPopularidadPayload(json, bundle ?? data);
    const encabezado =
      json?.success && encC ? flattenNestedEncabezadoApiShape(encC) : null;
    setEncabezadoCancelaciones(encabezado);
    return { list, apiBundle, encabezado };
  };

  const handleGastosJson = (json) => {
    const data = json?.success ? json.data : null;
    setPopularidadFooter({});
    setEncabezadoPopularidad(null);
    const parsed = parseJsonData(json);
    const list = coerceReportRows(parsed);
    setRows(list);
    setDescuentosApiBundle(null);
    setEncabezadoDescuentos(null);
    setEncabezadoCancelaciones(null);
    setCancelacionesApiBundle(null);
    setEncabezadoRelacionVentas(null);
    setRelacionVentasApiBundle(null);
    setRelacionVentasSoloTotales(null);
    setEncabezadoVentasPorHora(null);
    setVentasPorHoraApiBundle(null);
    setEncabezadoIngresoPorDia(null);
    setIngresoPorDiaApiBundle(null);
    const bundle =
      parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    const apiBundle = bundle
      ? { totales: bundle.totales ?? null, columnas: bundle.columnas ?? null }
      : null;
    setGastosApiBundle(apiBundle);
    const encG = extractEncabezadoFromPopularidadPayload(json, bundle ?? data);
    const encabezado =
      json?.success && encG ? flattenNestedEncabezadoApiShape(encG) : null;
    setEncabezadoGastos(encabezado);
    return { list, apiBundle, encabezado };
  };

  const handleRelacionVentasJson = (json) => {
    const data = json?.success ? json.data : null;
    setPopularidadFooter({});
    setEncabezadoPopularidad(null);
    const parsed = parseJsonData(json);
    const list = coerceReportRows(parsed);
    setRows(list);
    setDescuentosApiBundle(null);
    setEncabezadoDescuentos(null);
    setEncabezadoCancelaciones(null);
    setCancelacionesApiBundle(null);
    const bundle =
      parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    const apiBundle = bundle
      ? { totales: bundle.totales ?? null, columnas: bundle.columnas ?? null }
      : null;
    setRelacionVentasApiBundle(apiBundle);
    setRelacionVentasSoloTotales(
      json?.soloTotalesResumen && typeof json.soloTotalesResumen === 'object'
        ? json.soloTotalesResumen
        : null,
    );
    const encR = extractEncabezadoFromPopularidadPayload(json, bundle ?? data);
    const encabezado =
      json?.success && encR ? flattenNestedEncabezadoApiShape(encR) : null;
    setEncabezadoRelacionVentas(encabezado);
    setEncabezadoVentasPorHora(null);
    setVentasPorHoraApiBundle(null);
    setEncabezadoIngresoPorDia(null);
    setIngresoPorDiaApiBundle(null);
    return { list, apiBundle, encabezado };
  };

  const handleVentasPorHoraJson = (json) => {
    const data = json?.success ? json.data : null;
    setPopularidadFooter({});
    setEncabezadoPopularidad(null);
    const parsed = parseJsonData(json);
    const list = coerceReportRows(parsed);
    setRows(list);
    setDescuentosApiBundle(null);
    setEncabezadoDescuentos(null);
    setEncabezadoCancelaciones(null);
    setCancelacionesApiBundle(null);
    setEncabezadoRelacionVentas(null);
    setRelacionVentasApiBundle(null);
    setRelacionVentasSoloTotales(null);
    const bundle =
      parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    const apiBundle = bundle
      ? { totales: bundle.totales ?? null, columnas: bundle.columnas ?? null }
      : null;
    setVentasPorHoraApiBundle(apiBundle);
    const encV = extractEncabezadoFromPopularidadPayload(json, bundle ?? data);
    const encabezado =
      json?.success && encV ? flattenNestedEncabezadoApiShape(encV) : null;
    setEncabezadoVentasPorHora(encabezado);
    setEncabezadoIngresoPorDia(null);
    setIngresoPorDiaApiBundle(null);
    return { list, apiBundle, encabezado };
  };

  const handleProductosMeseroJson = (json) => {
    const data = json?.success ? json.data : null;
    setPopularidadFooter({});
    setEncabezadoPopularidad(null);
    const parsed = parseJsonData(json);
    const list = coerceReportRows(parsed);
    setRows(list);
    setDescuentosApiBundle(null);
    setEncabezadoDescuentos(null);
    setEncabezadoCancelaciones(null);
    setCancelacionesApiBundle(null);
    setEncabezadoRelacionVentas(null);
    setRelacionVentasApiBundle(null);
    setRelacionVentasSoloTotales(null);
    setEncabezadoVentasPorHora(null);
    setVentasPorHoraApiBundle(null);
    setEncabezadoIngresoPorDia(null);
    setIngresoPorDiaApiBundle(null);
    const enc = extractEncabezadoFromPopularidadPayload(json, data);
    const encabezado =
      enc && typeof enc === 'object' ? flattenNestedEncabezadoApiShape(enc) : null;
    setEncabezadoMesero(encabezado);
    return { list, encabezado };
  };

  const refrescarDescuentosCortesiasParaExport = async () => {
    if (!reporte) throw new Error('Reporte no disponible');
    const res = await reporte.fetcher(filters);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const { list, apiBundle, encabezado } = handleDescuentosCortesiasJson(json);
    const resolved = resolveDescuentosColumns(list.length > 0 ? list[0] : null);
    const rowsForExport = sortDescuentosRowsForDisplay(list, resolved, form.ordenarMesa);
    return {
      rowsForExport,
      encabezado,
      apiTotales: apiBundle?.totales ?? null,
      columnas: apiBundle?.columnas ?? null,
    };
  };

  const refrescarCancelacionesParaExport = async () => {
    if (!reporte) throw new Error('Reporte no disponible');
    const res = await reporte.fetcher(filters);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const { list, apiBundle, encabezado } = handleCancelacionesJson(json);
    return {
      rowsForExport: list,
      encabezado,
      apiTotales: apiBundle?.totales ?? null,
      columnas: apiBundle?.columnas ?? null,
    };
  };

  const refrescarGastosParaExport = async () => {
    if (!reporte) throw new Error('Reporte no disponible');
    const res = await reporte.fetcher(filters);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const { list, apiBundle, encabezado } = handleGastosJson(json);
    return {
      rowsForExport: list,
      encabezado,
      apiTotales: apiBundle?.totales ?? null,
      columnas: apiBundle?.columnas ?? null,
    };
  };

  const refrescarRelacionVentasParaExport = async () => {
    if (!reporte) throw new Error('Reporte no disponible');
    const res = await reporte.fetcher(filters);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const { list, apiBundle, encabezado } = handleRelacionVentasJson(json);
    const resolved = resolveRelacionVentasColumns(list.length > 0 ? list[0] : null);
    const rowsForExport = sortRelacionRowsForDisplay(list, resolved, form.ordenarMesa);
    return {
      rowsForExport,
      encabezado,
      apiTotales: apiBundle?.totales ?? null,
      columnas: apiBundle?.columnas ?? null,
    };
  };

  const refrescarVentasPorHoraParaExport = async () => {
    if (!reporte) throw new Error('Reporte no disponible');
    const res = await reporte.fetcher(filters);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const { list, apiBundle, encabezado } = handleVentasPorHoraJson(json);
    const resolved = resolveVentasPorHoraColumns(list.length > 0 ? list[0] : null);
    const rowsForExport = sortVentasPorHoraRowsForDisplay(list, resolved);
    return {
      rowsForExport,
      encabezado,
      apiTotales: apiBundle?.totales ?? null,
      columnas: apiBundle?.columnas ?? null,
    };
  };

  const handleIngresoPorDiaJson = (json) => {
    setPopularidadFooter({});
    setEncabezadoPopularidad(null);
    setDescuentosApiBundle(null);
    setEncabezadoDescuentos(null);
    setEncabezadoCancelaciones(null);
    setCancelacionesApiBundle(null);
    setEncabezadoRelacionVentas(null);
    setRelacionVentasApiBundle(null);
    setRelacionVentasSoloTotales(null);
    setEncabezadoVentasPorHora(null);
    setVentasPorHoraApiBundle(null);

    /* La respuesta real usa json.rows (no json.data) */
    const rawRows = json?.success && Array.isArray(json.rows) ? json.rows : [];
    setRows(rawRows);

    const apiBundle = json?.success
      ? { totales: json.totales ?? null, columnas: json.columnas ?? null }
      : null;
    setIngresoPorDiaApiBundle(apiBundle);

    /* Encabezado plano: { empresaNombre, sucursalNombre, periodoTexto, turnosTexto, cajasTexto, titulo } */
    const encabezado = json?.success && json.encabezado
      ? extractIngresoPorDiaEncabezado(json.encabezado)
      : null;
    setEncabezadoIngresoPorDia(encabezado);

    return { list: rawRows, apiBundle, encabezado };
  };

  const refrescarIngresoPorDiaParaExport = async () => {
    if (!reporte) throw new Error('Reporte no disponible');
    const res = await reporte.fetcher(filters);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const { list, apiBundle, encabezado } = handleIngresoPorDiaJson(json);
    return {
      rowsForExport: list,
      encabezado,
      apiTotales: apiBundle?.totales ?? null,
      columnas: apiBundle?.columnas ?? null,
    };
  };

  const refrescarMeseroParaExport = async () => {
    if (!reporte) throw new Error('Reporte no disponible');
    const res = await reporte.fetcher(filters);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return handleProductosMeseroJson(json);
  };

  const loadReport = async () => {
    if (!reporte) return;

    const necesitaCajasExplicitas = !reporteTieneFiltroCajasSuficiente({
      sucursalesTodas: form.sucursalesTodas,
      sucursalesSeleccionadas: form.sucursalesSeleccionadas,
      cajasTodas: form.cajasTodas,
      cajasSeleccionadas: form.cajasSeleccionadas,
    });
    if (necesitaCajasExplicitas) {
      setWarning('Marca al menos una caja de las sucursales seleccionadas.');
      return;
    }

    const requestId = ++loadReportRequestRef.current;
    const signatureAtRequest = filtersSignature;
    setLoading(true);
    setError('');
    setWarning('');
    try {
      const res = await reporte.fetcher(filters);
      if (requestId !== loadReportRequestRef.current) return;
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (requestId !== loadReportRequestRef.current) return;

      if (tipo === 'popularidad') {
        handlePopularidadJson(json);
      } else if (tipo === 'descuentos-cortesias') {
        handleDescuentosCortesiasJson(json);
      } else if (tipo === 'cancelaciones') {
        handleCancelacionesJson(json);
      } else if (tipo === 'gastos') {
        handleGastosJson(json);
      } else if (tipo === 'relacion-ventas') {
        handleRelacionVentasJson(json);
      } else if (tipo === 'ventas-por-hora') {
        handleVentasPorHoraJson(json);
      } else if (tipo === 'productos-vendidos-por-mesero') {
        handleProductosMeseroJson(json);
      } else if (tipo === 'ingreso-por-dia') {
        handleIngresoPorDiaJson(json);
      } else {
        setPopularidadFooter({});
        setEncabezadoPopularidad(null);
        const parsed = parseJsonData(json);
        const list = coerceReportRows(parsed);
        setRows(list);
        setDescuentosApiBundle(null);
        setEncabezadoDescuentos(null);
        setEncabezadoCancelaciones(null);
        setCancelacionesApiBundle(null);
        setEncabezadoGastos(null);
        setGastosApiBundle(null);
        setEncabezadoRelacionVentas(null);
        setRelacionVentasApiBundle(null);
    setRelacionVentasSoloTotales(null);
        setEncabezadoVentasPorHora(null);
        setVentasPorHoraApiBundle(null);
        setEncabezadoIngresoPorDia(null);
        setIngresoPorDiaApiBundle(null);
      }

      if (tipo !== 'productos-vendidos-por-mesero') {
        setEncabezadoMesero(null);
      }
      setAppliedFiltersSignature(signatureAtRequest);
    } catch (e) {
      if (requestId !== loadReportRequestRef.current) return;
      setRows([]);
      setEncabezadoMesero(null);
      setEncabezadoPopularidad(null);
      setEncabezadoDescuentos(null);
      setDescuentosApiBundle(null);
      setEncabezadoCancelaciones(null);
      setCancelacionesApiBundle(null);
      setEncabezadoGastos(null);
      setGastosApiBundle(null);
      setEncabezadoRelacionVentas(null);
      setRelacionVentasApiBundle(null);
    setRelacionVentasSoloTotales(null);
      setEncabezadoVentasPorHora(null);
      setVentasPorHoraApiBundle(null);
      setEncabezadoIngresoPorDia(null);
      setIngresoPorDiaApiBundle(null);
      setPopularidadFooter({});
      setError(e.message || 'No se pudo cargar el reporte');
    } finally {
      if (requestId === loadReportRequestRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setAppliedFiltersSignature('');
    setForm((prev) => ({
      ...prev,
      sucursalesTodas: true,
      sucursalesSeleccionadas: [],
      sucursalId: '',
      cajasTodas: true,
      cajasSeleccionadas: [],
      cajaInicio: null,
      cajaFin: null,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);

  useEffect(() => {
    if (reporte) {
      loadReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reporte, tipo]);


  const exportarPdfMeseroVista = async () => {
    if (!reporte || tipo !== 'productos-vendidos-por-mesero') return;
    setError('');
    setWarning('');
    setTablaReporteExportBusy(true);
    try {
      const { list, encabezado } = await refrescarMeseroParaExport();
      if (!list.length) {
        setWarning('No hay datos para generar este reporte.');
        return;
      }
      const fechaTxtMesero = (isoADisplayDDMMAAAA(form.fechaInicio) || form.fechaInicio || '—').replaceAll('/', '-');
      const fechaFinTxtMesero = (isoADisplayDDMMAAAA(form.fechaFin) || form.fechaFin || '—').replaceAll('/', '-');
      const turnoIniLbl = gastosCatalogo.turnoInicioNombre || getTurnoNombreDisplay(form.turnoInicio);
      const turnoFinLbl = gastosCatalogo.turnoFinalNombre || getTurnoNombreDisplay(form.turnoFin);
      const turnosTxtMesero = `${form.turnoInicio} ${turnoIniLbl} a ${form.turnoFin} ${turnoFinLbl}`;
      const encReal = encabezado && typeof encabezado === 'object' ? encabezado : {};
      const encabezadoMeseroExport = {
        ...MESERO_HEADER_FIJO,
        ...encReal,
        sucursalesTexto: popularidadEncabezadoFallback.sucursalesTexto,
        sucursalesItems: popularidadEncabezadoFallback.sucursalesItems,
        cajasTexto: popularidadEncabezadoFallback.cajasTexto || encReal.cajasTexto,
        cajasItems: popularidadEncabezadoFallback.cajasItems,
        periodoTexto: `Del: ${fechaTxtMesero} al ${fechaFinTxtMesero}`,
        turnosTexto: encReal.turnosTexto || turnosTxtMesero,
      };
      downloadProductosMeseroPdf({
        rows: list,
        encabezado: encabezadoMeseroExport,
        fechaInicio: form.fechaInicio,
        fechaFin: form.fechaFin,
        cajaInicio: form.cajaInicio,
        cajaFin: form.cajaFin,
        turnoInicio: form.turnoInicio,
        turnoFin: form.turnoFin,
        globalSoloTotalesPorFamilia: meseroGlobalSoloTotalesFamilia,
        noGlobalAsterisco: form.noGlobalAsterisco,
        mayorIngreso: form.mayorIngreso,
        mayorVenta: form.mayorVenta,
        ordenMeseros: form.ordenMeseros,
      });
    } catch (e) {
      setError(e.message || 'No se pudo generar el PDF');
    } finally {
      setTablaReporteExportBusy(false);
    }
  };

  const exportarPdfPopularidad = async () => {
    if (!reporte || tipo !== 'popularidad') return;
    setError('');
    setWarning('');
    setTablaReporteExportBusy(true);
    try {
      const { rowsForExport, footer, encabezado } = await refrescarPopularidadParaExport();
      if (!rowsForExport.length) {
        setWarning('No hay datos para generar este reporte.');
        return;
      }
      downloadPopularidadPdf(rowsForExport, {
        fechaInicio: form.fechaInicio,
        fechaFin: form.fechaFin,
        cajaInicio: form.cajaInicio,
        cajaFin: form.cajaFin,
        turnoInicio: form.turnoInicio,
        turnoFin: form.turnoFin,
        fileBase: reporte.endpoint,
        encabezado,
        encabezadoFallback: popularidadEncabezadoFallback,
        sucursalNombre: selectedSuc?.nombreSucursal ?? '',
        reporteTitulo: 'REPORTE POPULARIDAD DE PRODUCTOS',
        incluirCortesias: form.incluirCortesias,
        bebidasAlcoholicas: form.bebidasAlcoholicas,
        incluirPrecioMenu: form.incluirPrecioMenu,
        footer,
      });
    } catch (e) {
      setError(e.message || 'No se pudo generar el PDF');
    } finally {
      setTablaReporteExportBusy(false);
    }
  };

  const exportarPdfDescuentosCortesias = async () => {
    if (!reporte || tipo !== 'descuentos-cortesias') return;
    setError('');
    setWarning('');
    setTablaReporteExportBusy(true);
    try {
      const { rowsForExport, encabezado, apiTotales, columnas } =
        await refrescarDescuentosCortesiasParaExport();
      if (!rowsForExport.length) {
        setWarning('No hay datos para generar este reporte.');
        return;
      }
      downloadDescuentosCortesiasPdf(rowsForExport, {
        fechaInicio: form.fechaInicio,
        fechaFin: form.fechaFin,
        cajaInicio: form.cajaInicio,
        cajaFin: form.cajaFin,
        turnoInicio: form.turnoInicio,
        turnoFin: form.turnoFin,
        fileBase: reporte.endpoint,
        encabezado,
        encabezadoFallback: popularidadEncabezadoFallback,
        sucursalNombre: selectedSuc?.nombreSucursal ?? '',
        reporteTitulo: 'RELACIÓN DE DESCUENTOS Y CORTESÍAS',
        apiTotales,
        columnas,
      });
    } catch (e) {
      setError(e.message || 'No se pudo generar el PDF');
    } finally {
      setTablaReporteExportBusy(false);
    }
  };

  const exportarPdfRelacionVentasSoloTotales = async () => {
    if (!reporte || tipo !== 'relacion-ventas') return;
    setError('');
    setWarning('');
    setTablaReporteExportBusy(true);
    try {
      const res = await reporte.fetcher(filters);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const { encabezado } = handleRelacionVentasJson(json);
      const resumen =
        json?.soloTotalesResumen && typeof json.soloTotalesResumen === 'object'
          ? json.soloTotalesResumen
          : null;
      if (!resumen) {
        setWarning('No hay datos para generar este reporte.');
        return;
      }
      downloadRelacionVentasSoloTotalesPdf(resumen, {
        fechaInicio: form.fechaInicio,
        fechaFin: form.fechaFin,
        cajaInicio: form.cajaInicio,
        cajaFin: form.cajaFin,
        turnoInicio: form.turnoInicio,
        turnoFin: form.turnoFin,
        fileBase: `${reporte.endpoint}-solo-totales-${filters.fechaInicio}-${filters.fechaFin}`,
        encabezado,
        encabezadoFallback: popularidadEncabezadoFallback,
        sucursalNombre: selectedSuc?.nombreSucursal ?? '',
        ocultarFolios: soloTotalesOcultarFolios,
      });
    } catch (e) {
      setError(e.message || 'No se pudo generar el PDF');
    } finally {
      setTablaReporteExportBusy(false);
    }
  };

  const exportarPdfRelacionVentas = async () => {
    if (!reporte || tipo !== 'relacion-ventas') return;
    setError('');
    setWarning('');
    setTablaReporteExportBusy(true);
    try {
      const { rowsForExport, encabezado, apiTotales, columnas } =
        await refrescarRelacionVentasParaExport();
      if (!rowsForExport.length) {
        setWarning('No hay datos para generar este reporte.');
        return;
      }
      downloadRelacionVentasPdf(rowsForExport, {
        fechaInicio: form.fechaInicio,
        fechaFin: form.fechaFin,
        cajaInicio: form.cajaInicio,
        cajaFin: form.cajaFin,
        turnoInicio: form.turnoInicio,
        turnoFin: form.turnoFin,
        fileBase: reporte.endpoint,
        encabezado,
        encabezadoFallback: popularidadEncabezadoFallback,
        sucursalNombre: selectedSuc?.nombreSucursal ?? '',
        reporteTitulo: RELACION_VENTAS_TITULO_DEFAULT,
        apiTotales,
        columnas,
        ordenarMesa: form.ordenarMesa,
        agruparTurno: form.agruparTurno,
      });
    } catch (e) {
      setError(e.message || 'No se pudo generar el PDF');
    } finally {
      setTablaReporteExportBusy(false);
    }
  };

  const exportarPdfCancelaciones = async () => {
    if (!reporte || tipo !== 'cancelaciones') return;
    setError('');
    setWarning('');
    setTablaReporteExportBusy(true);
    try {
      const { rowsForExport, encabezado, apiTotales, columnas } =
        await refrescarCancelacionesParaExport();
      if (!rowsForExport.length) {
        setWarning('No hay datos para generar este reporte.');
        return;
      }
      downloadCancelacionesPdf(rowsForExport, {
        fechaInicio: form.fechaInicio,
        fechaFin: form.fechaFin,
        cajaInicio: form.cajaInicio,
        cajaFin: form.cajaFin,
        turnoInicio: form.turnoInicio,
        turnoFin: form.turnoFin,
        fileBase: reporte.endpoint,
        encabezado,
        encabezadoFallback: popularidadEncabezadoFallback,
        sucursalNombre: selectedSuc?.nombreSucursal ?? '',
        reporteTitulo: CANCELACIONES_REPORT_TITULO_DEFAULT,
        apiTotales,
        columnas,
      });
    } catch (e) {
      setError(e.message || 'No se pudo generar el PDF');
    } finally {
      setTablaReporteExportBusy(false);
    }
  };

  const exportarPdfGastos = async () => {
    if (!reporte || tipo !== 'gastos') return;
    setError('');
    setWarning('');
    setTablaReporteExportBusy(true);
    try {
      const { rowsForExport, encabezado, apiTotales, columnas } =
        await refrescarGastosParaExport();
      if (!rowsForExport.length) {
        setWarning('No hay datos para generar este reporte.');
        return;
      }
      downloadGastosPdf(rowsForExport, {
        fechaInicio: form.fechaInicio,
        fechaFin: form.fechaFin,
        cajaInicio: form.cajaInicio,
        cajaFin: form.cajaFin,
        turnoInicio: form.turnoInicio,
        turnoFin: form.turnoFin,
        fileBase: reporte.endpoint,
        encabezado,
        encabezadoFallback: popularidadEncabezadoFallback,
        sucursalNombre: selectedSuc?.nombreSucursal ?? '',
        reporteTitulo: GASTOS_REPORT_TITULO_DEFAULT,
        apiTotales,
        columnas,
        globalView: form.gastosGlobal,
      });
    } catch (e) {
      setError(e.message || 'No se pudo generar el PDF');
    } finally {
      setTablaReporteExportBusy(false);
    }
  };

  const exportarPdfVentasPorHora = async () => {
    if (!reporte || tipo !== 'ventas-por-hora') return;
    setError('');
    setWarning('');
    setTablaReporteExportBusy(true);
    try {
      const { rowsForExport, encabezado, apiTotales, columnas } =
        await refrescarVentasPorHoraParaExport();
      if (!rowsForExport.length) {
        setWarning('No hay datos para generar este reporte.');
        return;
      }
      downloadVentasPorHoraPdf(rowsForExport, {
        fechaInicio: form.fechaInicio,
        fechaFin: form.fechaFin,
        cajaInicio: form.cajaInicio,
        cajaFin: form.cajaFin,
        turnoInicio: form.turnoInicio,
        turnoFin: form.turnoFin,
        fileBase: reporte.endpoint,
        encabezado,
        encabezadoFallback: popularidadEncabezadoFallback,
        sucursalNombre: selectedSuc?.nombreSucursal ?? '',
        reporteTitulo: VENTAS_POR_HORA_TITULO_DEFAULT,
        apiTotales,
        columnas,
      });
    } catch (e) {
      setError(e.message || 'No se pudo generar el PDF');
    } finally {
      setTablaReporteExportBusy(false);
    }
  };

  const exportarPdfIngresoPorDia = async () => {
    if (!reporte || tipo !== 'ingreso-por-dia') return;
    setError('');
    setWarning('');
    setTablaReporteExportBusy(true);
    try {
      const { rowsForExport, encabezado, apiTotales, columnas } =
        await refrescarIngresoPorDiaParaExport();
      if (!rowsForExport.length) {
        setWarning('No hay datos para generar este reporte.');
        return;
      }
      const pdfBase = form.vistaIngreso === 'total' ? 'ingresos-total' : 'ingresos-por-dia';
      const pdfFileBase = `${pdfBase}-${filters.fechaInicio}-${filters.fechaFin}`;
      downloadIngresoPorDiaPdf(rowsForExport, {
        fileBase: pdfFileBase,
        encabezadoApi: encabezado,
        encabezadoFallback: popularidadEncabezadoFallback,
        reporteTitulo: 'INGRESOS',
        apiTotales,
        columnas,
        vistaIngreso: form.vistaIngreso,
      });
    } catch (e) {
      setError(e.message || 'No se pudo generar el PDF');
    } finally {
      setTablaReporteExportBusy(false);
    }
  };

  const exportarExcelLocal = async () => {
    const ep = reporte?.endpoint;

    if (tipo === 'productos-vendidos-por-mesero' || ep === 'report-products-sold-by-waiter') {
      setError('');
      setWarning('');
      setTablaReporteExportBusy(true);
      try {
        const { list, encabezado } = await refrescarMeseroParaExport();
        if (!list.length) {
          setWarning('No hay datos para generar este reporte.');
          return;
        }
        const fechaTxtMesero = (isoADisplayDDMMAAAA(form.fechaInicio) || form.fechaInicio || '—').replaceAll('/', '-');
        const fechaFinTxtMesero = (isoADisplayDDMMAAAA(form.fechaFin) || form.fechaFin || '—').replaceAll('/', '-');
        const turnoIniLbl = gastosCatalogo.turnoInicioNombre || getTurnoNombreDisplay(form.turnoInicio);
        const turnoFinLbl = gastosCatalogo.turnoFinalNombre || getTurnoNombreDisplay(form.turnoFin);
        const turnosTxtMesero = `${form.turnoInicio} ${turnoIniLbl} a ${form.turnoFin} ${turnoFinLbl}`;
        const encRealExcel = encabezado && typeof encabezado === 'object' ? encabezado : {};
        const encabezadoMeseroExport = {
          ...MESERO_HEADER_FIJO,
          ...encRealExcel,
          sucursalesTexto: popularidadEncabezadoFallback.sucursalesTexto,
          sucursalesItems: popularidadEncabezadoFallback.sucursalesItems,
          cajasTexto: popularidadEncabezadoFallback.cajasTexto || encRealExcel.cajasTexto,
          cajasItems: popularidadEncabezadoFallback.cajasItems,
          periodoTexto: `Del: ${fechaTxtMesero} al ${fechaFinTxtMesero}`,
          turnosTexto: encRealExcel.turnosTexto || turnosTxtMesero,
        };
        const { aoa: meseroAoa, mergeTitleRow, colWidths, excelRowStyles } = buildProductosMeseroExcelAoa(list, {
          globalSoloTotalesPorFamilia: meseroGlobalSoloTotalesFamilia,
          noGlobalAsterisco: form.noGlobalAsterisco,
          mayorIngreso: form.mayorIngreso,
          mayorVenta: form.mayorVenta,
          ordenMeseros: form.ordenMeseros,
          encabezado: encabezadoMeseroExport,
          fechaInicio: form.fechaInicio,
          fechaFin: form.fechaFin,
          cajaInicio: form.cajaInicio,
          cajaFin: form.cajaFin,
          turnoInicio: form.turnoInicio,
          turnoFin: form.turnoFin,
        });
        const ws = XLSX.utils.aoa_to_sheet(meseroAoa);
        if (mergeTitleRow?.length) ws['!merges'] = mergeTitleRow;
        if (Array.isArray(colWidths) && colWidths.length > 0) {
          ws['!cols'] = colWidths;
        }

        const tableCols = Array.isArray(colWidths) && colWidths.length > 0
          ? colWidths.length
          : Math.max(1, ...(meseroAoa || []).map((r) => (Array.isArray(r) ? r.length : 0)));
        const toCellAddr = (r, c) => XLSX.utils.encode_cell({ r, c });
        const ensureCell = (r, c) => {
          const addr = toCellAddr(r, c);
          if (!ws[addr]) ws[addr] = { t: 's', v: '' };
          return ws[addr];
        };
        const isHeaderRowGlobal = (row) => {
          if (!Array.isArray(row)) return false;
          const a0 = String(row[0] ?? '').trim().toLowerCase();
          const a1 = String(row[1] ?? '').trim().toLowerCase();
          return a0 === 'familia' && (a1 === 'cant.' || a1 === 'cantidad');
        };
        const isHeaderRowNoGlobal = (row) => {
          if (!Array.isArray(row)) return false;
          const values = row.map((v) => String(v ?? '').trim().toLowerCase());
          const planoClasico =
            values[0] === 'familia' &&
            values[1] === 'codigo' &&
            values[2] === 'nombreproducto';
          const noGlobalAsteriscoHeader =
            values[0] === 'código' &&
            values[1] === 'nombre' &&
            (values[2] === 'cant.' || values[2] === 'cant');
          return planoClasico || noGlobalAsteriscoHeader;
        };
        const isTotalGeneralRow = (row) => {
          if (!Array.isArray(row)) return false;
          const label = String(row[0] ?? '').trim().toUpperCase();
          return label.startsWith('TOTAL GENERAL') || label.startsWith('TOTAL REPORTE');
        };
        const isTotalMeseroRow = (row) => {
          if (!Array.isArray(row)) return false;
          const label = String(row[0] ?? '').trim().toUpperCase();
          return label.startsWith('TOTAL MESERO');
        };
        const NAVY_FILL = 'FF1E293B';
        const NAVY_BORDER = 'FF334155';
        const NAVY_SOFT_FILL = 'FF334155';
        const NAVY_SOFT_BORDER = 'FF475569';
        const TOTAL_GENERAL_FILL = 'FF1D4ED8';
        const TOTAL_GENERAL_BORDER = 'FF1E40AF';
        const LIGHT_GRID_BORDER = 'FFE5E7EB';

        // Encabezado centrado (filas fusionadas).
        for (const m of mergeTitleRow || []) {
          const rowIdx = m?.s?.r;
          if (!Number.isInteger(rowIdx)) continue;
          if (excelRowStyles && excelRowStyles[rowIdx]) continue;
          const rowVal = Array.isArray(meseroAoa?.[rowIdx]) ? String(meseroAoa[rowIdx][0] ?? '') : '';
          const rowTxt = rowVal.trim().toUpperCase();
          const isMeseroRow = rowTxt.startsWith('MESERO:');
          const isFamiliaDash = rowTxt.startsWith('——') || rowTxt.startsWith('--');
          for (let c = 0; c < tableCols; c++) {
            const cell = ensureCell(rowIdx, c);
            cell.s = {
              ...(cell.s || {}),
              alignment: {
                horizontal: isMeseroRow || isFamiliaDash ? 'left' : 'center',
                vertical: 'center',
                wrapText: true,
              },
              font: { ...(cell.s?.font || {}), bold: true, color: { rgb: 'FF0F172A' } },
            };
          }
        }

        // Encabezados de tabla en azul (global y no-global).
        meseroAoa.forEach((row, r) => {
          if (!isHeaderRowGlobal(row) && !isHeaderRowNoGlobal(row)) return;
          const lowerVals = Array.isArray(row)
            ? row.map((v) => String(v ?? '').trim().toLowerCase())
            : [];
          const isDetalle8ColHeader =
            lowerVals[0] === 'código' && lowerVals[1] === 'nombre';
          const isPlano12ColHeader =
            lowerVals[0] === 'familia' &&
            lowerVals[1] === 'codigo' &&
            lowerVals[2] === 'nombreproducto';
          /** Vista global Excel: Familia | Cant. | T.Costo | Importe | Utilidad — números a la derecha. */
          const isGlobalFamiliaTotalesHeader =
            lowerVals[0] === 'familia' &&
            (lowerVals[1] === 'cant.' ||
              lowerVals[1] === 'cantidad' ||
              lowerVals[1] === 'cant');
          for (let c = 0; c < tableCols; c++) {
            const cell = ensureCell(r, c);
            const headerAlignLeftPair =
              isHeaderRowNoGlobal(row) && (c === 0 || c === 1);
            cell.s = {
              ...(cell.s || {}),
              alignment: {
                horizontal: isGlobalFamiliaTotalesHeader
                  ? c === 0
                    ? 'left'
                    : 'right'
                  : isPlano12ColHeader
                    ? 'left'
                    : isDetalle8ColHeader
                      ? c <= 1
                        ? 'left'
                        : 'right'
                      : headerAlignLeftPair
                        ? 'left'
                        : c === 0
                          ? 'left'
                          : 'center',
                vertical: 'center',
                wrapText: true,
              },
              font: { ...(cell.s?.font || {}), bold: true, color: { rgb: 'FFFFFFFF' } },
              fill: { fgColor: { rgb: NAVY_FILL } },
              border: {
                top: { style: 'thin', color: { rgb: NAVY_BORDER } },
                bottom: { style: 'thin', color: { rgb: NAVY_BORDER } },
                left: { style: 'thin', color: { rgb: NAVY_BORDER } },
                right: { style: 'thin', color: { rgb: NAVY_BORDER } },
              },
            };
          }
        });

        // Fila TOTAL GENERAL en el mismo azul.
        meseroAoa.forEach((row, r) => {
          if (!isTotalGeneralRow(row)) return;
          for (let c = 0; c < tableCols; c++) {
            const cell = ensureCell(r, c);
            cell.s = {
              ...(cell.s || {}),
              alignment: { horizontal: c === 0 ? 'left' : 'right', vertical: 'center', wrapText: true },
              font: { ...(cell.s?.font || {}), bold: true, color: { rgb: 'FFFFFFFF' } },
              fill: { fgColor: { rgb: TOTAL_GENERAL_FILL } },
              border: {
                top: { style: 'thin', color: { rgb: TOTAL_GENERAL_BORDER } },
                bottom: { style: 'thin', color: { rgb: TOTAL_GENERAL_BORDER } },
                left: { style: 'thin', color: { rgb: TOTAL_GENERAL_BORDER } },
                right: { style: 'thin', color: { rgb: TOTAL_GENERAL_BORDER } },
              },
            };
          }
        });

        // Fila TOTAL MESERO en azul un poco menos intenso.
        meseroAoa.forEach((row, r) => {
          if (!isTotalMeseroRow(row)) return;
          for (let c = 0; c < tableCols; c++) {
            const cell = ensureCell(r, c);
            cell.s = {
              ...(cell.s || {}),
              alignment: { horizontal: c === 0 ? 'left' : 'right', vertical: 'center', wrapText: true },
              font: { ...(cell.s?.font || {}), bold: true, color: { rgb: 'FFFFFFFF' } },
              fill: { fgColor: { rgb: NAVY_SOFT_FILL } },
              border: {
                top: { style: 'thin', color: { rgb: NAVY_SOFT_BORDER } },
                bottom: { style: 'thin', color: { rgb: NAVY_SOFT_BORDER } },
                left: { style: 'thin', color: { rgb: NAVY_SOFT_BORDER } },
                right: { style: 'thin', color: { rgb: NAVY_SOFT_BORDER } },
              },
            };
          }
        });

        if (meseroGlobalSoloTotalesFamilia && tableCols === 5) {
          for (let r = 0; r < meseroAoa.length; r++) {
            const row = meseroAoa[r];
            if (!Array.isArray(row) || row.length < 5) continue;
            const cell0 = String(row[0] ?? '').trim();
            if (!cell0) continue;
            const u0 = cell0.toUpperCase();
            if (u0.startsWith('MESERO:')) continue;
            if (isHeaderRowGlobal(row)) continue;
            if (u0.startsWith('TOTAL')) continue;
            if (excelRowStyles && excelRowStyles[r]) continue;
            const restEmpty = [1, 2, 3, 4].every((i) => String(row[i] ?? '').trim() === '');
            if (restEmpty) continue;
            for (let c = 0; c < 5; c++) {
              const cell = ensureCell(r, c);
              cell.s = {
                ...(cell.s || {}),
                alignment: {
                  horizontal: c === 0 ? 'left' : 'right',
                  vertical: 'center',
                  wrapText: true,
                },
                font: { ...(cell.s?.font || {}), color: { rgb: 'FF0F172A' } },
                fill: { fgColor: { rgb: 'FFFFFFFF' } },
                border: {
                  top: { style: 'thin', color: { rgb: LIGHT_GRID_BORDER } },
                  bottom: { style: 'thin', color: { rgb: LIGHT_GRID_BORDER } },
                  left: { style: 'thin', color: { rgb: LIGHT_GRID_BORDER } },
                  right: { style: 'thin', color: { rgb: LIGHT_GRID_BORDER } },
                },
              };
            }
          }
        }

        const plano12HeaderIdx = meseroAoa.findIndex(
          (row) =>
            Array.isArray(row) &&
            String(row[0] ?? '').trim().toLowerCase() === 'familia' &&
            String(row[1] ?? '').trim().toLowerCase() === 'codigo' &&
            String(row[2] ?? '').trim().toLowerCase() === 'nombreproducto'
        );

        if (form.noGlobalAsterisco && plano12HeaderIdx >= 0) {
          const numericColsPlano12 = new Set([4, 5, 6, 7, 8, 9, 10]);
          for (let r = plano12HeaderIdx + 1; r < meseroAoa.length; r++) {
            const row = meseroAoa[r];
            if (!Array.isArray(row) || row.length < 12) continue;
            const label = String(row[0] ?? '').trim().toUpperCase();
            if (label.startsWith('TOTAL')) continue;
            for (let c = 0; c < tableCols; c++) {
              const cell = ensureCell(r, c);
              cell.s = {
                ...(cell.s || {}),
                alignment: {
                  horizontal: numericColsPlano12.has(c) ? 'right' : 'left',
                  vertical: 'center',
                  wrapText: true,
                },
                font: { ...(cell.s?.font || {}), color: { rgb: 'FF0F172A' } },
                fill: { fgColor: { rgb: 'FFFFFFFF' } },
                border: {
                  top: { style: 'thin', color: { rgb: LIGHT_GRID_BORDER } },
                  bottom: { style: 'thin', color: { rgb: LIGHT_GRID_BORDER } },
                  left: { style: 'thin', color: { rgb: LIGHT_GRID_BORDER } },
                  right: { style: 'thin', color: { rgb: LIGHT_GRID_BORDER } },
                },
              };
            }
          }
        }

        // Detallado ejecutivo: barra MESERO navy, APARTADO y familia según captura institucional.
        Object.entries(excelRowStyles || {}).forEach(([rk, role]) => {
          const r = Number(rk);
          if (!Number.isInteger(r) || r < 0) return;
          if (!Array.isArray(meseroAoa[r])) return;

          if (role === 'meseroBar') {
            for (let c = 0; c < tableCols; c++) {
              const cell = ensureCell(r, c);
              cell.s = {
                alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
                font: { bold: true, color: { rgb: 'FFFFFFFF' } },
                fill: { fgColor: { rgb: NAVY_FILL } },
                border: {
                  top: { style: 'thin', color: { rgb: NAVY_BORDER } },
                  bottom: { style: 'thin', color: { rgb: NAVY_BORDER } },
                  left: { style: 'thin', color: { rgb: NAVY_BORDER } },
                  right: { style: 'thin', color: { rgb: NAVY_BORDER } },
                },
              };
            }
            return;
          }

          if (role === 'apartadoLabel') {
            for (let c = 0; c < tableCols; c++) {
              const cell = ensureCell(r, c);
              cell.s = {
                ...(cell.s || {}),
                alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
                font: { bold: true, color: { rgb: 'FF0F172A' } },
                fill: { fgColor: { rgb: 'FFFFFFFF' } },
                border: {
                  bottom: { style: 'thin', color: { rgb: LIGHT_GRID_BORDER } },
                },
              };
            }
            return;
          }

          if (role === 'familiaRow') {
            for (let c = 0; c < tableCols; c++) {
              const cell = ensureCell(r, c);
              cell.s = {
                ...(cell.s || {}),
                alignment: {
                  horizontal: 'left',
                  vertical: 'center',
                  wrapText: true,
                  indent: 0,
                },
                font: { bold: true, color: { rgb: 'FF0F172A' } },
                fill: { fgColor: { rgb: 'FFFFFFFF' } },
                border: {
                  bottom: { style: 'thin', color: { rgb: LIGHT_GRID_BORDER } },
                },
              };
            }
            return;
          }

          if (role === 'totalFamilia') {
            const numericCols = new Set([2, 4, 6, 7]);
            for (let c = 0; c < tableCols; c++) {
              const cell = ensureCell(r, c);
              cell.s = {
                ...(cell.s || {}),
                alignment: {
                  horizontal: numericCols.has(c) ? 'right' : 'left',
                  vertical: 'center',
                  wrapText: true,
                },
                font: { bold: true, color: { rgb: 'FF0F172A' } },
                fill: { fgColor: { rgb: 'FFE8EDF3' } },
                border: {
                  top: { style: 'thin', color: { rgb: LIGHT_GRID_BORDER } },
                  bottom: { style: 'thin', color: { rgb: LIGHT_GRID_BORDER } },
                  left: { style: 'thin', color: { rgb: LIGHT_GRID_BORDER } },
                  right: { style: 'thin', color: { rgb: LIGHT_GRID_BORDER } },
                },
              };
            }
          }
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
          wb,
          ws,
          meseroGlobalSoloTotalesFamilia
            ? 'Global familias'
            : form.noGlobalAsterisco
              ? 'Meseros plano'
              : 'Meseros ejecutivo'
        );
        const meseroDetalleSufijo = meseroGlobalSoloTotalesFamilia
          ? 'global-'
          : form.noGlobalAsterisco
            ? 'plano-'
            : 'ejecutivo-';
        const fileName = `${reporte.endpoint}-${meseroDetalleSufijo}${filters.fechaInicio}-${filters.fechaFin}.xlsx`;
        XLSX.writeFile(wb, fileName);
      } catch (e) {
        setError(e?.message || 'No se pudo generar el Excel.');
      } finally {
        setTablaReporteExportBusy(false);
      }
      return;
    }

    if (tipo === 'relacion-ventas' || ep === 'relacion-ventas') {
      if (form.soloTotales) {
        setWarning('Use el botón PDF para exportar el resumen de solo totales.');
        return;
      }
      setError('');
      setWarning('');
      setTablaReporteExportBusy(true);
      try {
        const { rowsForExport, encabezado, apiTotales, columnas } =
          await refrescarRelacionVentasParaExport();
        if (!rowsForExport.length) {
          setWarning('No hay datos para generar este reporte.');
          return;
        }
        const fileName = `${reporte.endpoint}-${filters.fechaInicio}-${filters.fechaFin}.xlsx`;
        downloadRelacionVentasExcelFile(
          rowsForExport,
          {
            fechaInicio: form.fechaInicio,
            fechaFin: form.fechaFin,
            cajaInicio: form.cajaInicio,
            cajaFin: form.cajaFin,
            turnoInicio: form.turnoInicio,
            turnoFin: form.turnoFin,
            encabezado,
            encabezadoFallback: popularidadEncabezadoFallback,
            sucursalNombre: selectedSuc?.nombreSucursal ?? '',
            reporteTitulo: RELACION_VENTAS_TITULO_DEFAULT,
            apiTotales,
            columnas,
            ordenarMesa: form.ordenarMesa,
            agruparTurno: form.agruparTurno,
          },
          fileName,
          excelSheetName(reporte.title)
        );
      } catch (e) {
        setError(e?.message || 'No se pudo generar el Excel.');
      } finally {
        setTablaReporteExportBusy(false);
      }
      return;
    }

    if (tipo === 'cancelaciones' || ep === 'cancelaciones') {
      setError('');
      setWarning('');
      setTablaReporteExportBusy(true);
      try {
        const { rowsForExport, encabezado, apiTotales, columnas } =
          await refrescarCancelacionesParaExport();
        if (!rowsForExport.length) {
          setWarning('No hay datos para generar este reporte.');
          return;
        }
        const fileName = `${reporte.endpoint}-${filters.fechaInicio}-${filters.fechaFin}.xlsx`;
        downloadCancelacionesExcelFile(
          rowsForExport,
          {
            fechaInicio: form.fechaInicio,
            fechaFin: form.fechaFin,
            cajaInicio: form.cajaInicio,
            cajaFin: form.cajaFin,
            turnoInicio: form.turnoInicio,
            turnoFin: form.turnoFin,
            encabezado,
            encabezadoFallback: popularidadEncabezadoFallback,
            sucursalNombre: selectedSuc?.nombreSucursal ?? '',
            reporteTitulo: CANCELACIONES_REPORT_TITULO_DEFAULT,
            apiTotales,
            columnas,
          },
          fileName,
          excelSheetName(reporte.title)
        );
      } catch (e) {
        setError(e?.message || 'No se pudo generar el Excel.');
      } finally {
        setTablaReporteExportBusy(false);
      }
      return;
    }

    if (tipo === 'gastos' || ep === 'gastos') {
      setError('');
      setWarning('');
      setTablaReporteExportBusy(true);
      try {
        const { rowsForExport, encabezado, apiTotales, columnas } =
          await refrescarGastosParaExport();
        if (!rowsForExport.length) {
          setWarning('No hay datos para generar este reporte.');
          return;
        }
        const fileName = `${reporte.endpoint}-${filters.fechaInicio}-${filters.fechaFin}.xlsx`;
        downloadGastosExcelFile(
          rowsForExport,
          {
            fechaInicio: form.fechaInicio,
            fechaFin: form.fechaFin,
            cajaInicio: form.cajaInicio,
            cajaFin: form.cajaFin,
            turnoInicio: form.turnoInicio,
            turnoFin: form.turnoFin,
            encabezado,
            encabezadoFallback: popularidadEncabezadoFallback,
            sucursalNombre: selectedSuc?.nombreSucursal ?? '',
            reporteTitulo: GASTOS_REPORT_TITULO_DEFAULT,
            apiTotales,
            columnas,
            globalView: form.gastosGlobal,
          },
          fileName,
          excelSheetName(reporte.title)
        );
      } catch (e) {
        setError(e?.message || 'No se pudo generar el Excel.');
      } finally {
        setTablaReporteExportBusy(false);
      }
      return;
    }

    if (tipo === 'descuentos-cortesias' || ep === 'descuentos-cortesias') {
      setError('');
      setWarning('');
      setTablaReporteExportBusy(true);
      try {
        const { rowsForExport, encabezado, apiTotales, columnas } =
          await refrescarDescuentosCortesiasParaExport();
        if (!rowsForExport.length) {
          setWarning('No hay datos para generar este reporte.');
          return;
        }
        const fileName = `${reporte.endpoint}-${filters.fechaInicio}-${filters.fechaFin}.xlsx`;
        downloadDescuentosCortesiasExcelFile(
          rowsForExport,
          {
            fechaInicio: form.fechaInicio,
            fechaFin: form.fechaFin,
            cajaInicio: form.cajaInicio,
            cajaFin: form.cajaFin,
            turnoInicio: form.turnoInicio,
            turnoFin: form.turnoFin,
            encabezado,
            encabezadoFallback: popularidadEncabezadoFallback,
            sucursalNombre: selectedSuc?.nombreSucursal ?? '',
            reporteTitulo: 'RELACIÓN DE DESCUENTOS Y CORTESÍAS',
            apiTotales,
            columnas,
          },
          fileName,
          excelSheetName(reporte.title)
        );
      } catch (e) {
        setError(e?.message || 'No se pudo generar el Excel.');
      } finally {
        setTablaReporteExportBusy(false);
      }
      return;
    }

    if (tipo === 'popularidad' || ep === 'popularidad') {
      setError('');
      setWarning('');
      setTablaReporteExportBusy(true);
      try {
        const { rowsForExport, footer, encabezado } = await refrescarPopularidadParaExport();
        if (!rowsForExport.length) {
          setWarning('No hay datos para generar este reporte.');
          return;
        }
        const fileName = `${reporte.endpoint}-${filters.fechaInicio}-${filters.fechaFin}.xlsx`;
        downloadPopularidadExcelFile(
          rowsForExport,
          {
            fechaInicio: form.fechaInicio,
            fechaFin: form.fechaFin,
            cajaInicio: form.cajaInicio,
            cajaFin: form.cajaFin,
            turnoInicio: form.turnoInicio,
            turnoFin: form.turnoFin,
            encabezado,
            encabezadoFallback: popularidadEncabezadoFallback,
            reporteTitulo: 'REPORTE POPULARIDAD DE PRODUCTOS',
            incluirCortesias: form.incluirCortesias,
            bebidasAlcoholicas: form.bebidasAlcoholicas,
            incluirPrecioMenu: form.incluirPrecioMenu,
            footer,
          },
          fileName,
          excelSheetName(reporte.title)
        );
      } catch (e) {
        setError(e?.message || 'No se pudo generar el Excel.');
      } finally {
        setTablaReporteExportBusy(false);
      }
      return;
    }

    if (tipo === 'ventas-por-hora' || ep === 'ventas-por-hora') {
      setError('');
      setWarning('');
      setTablaReporteExportBusy(true);
      try {
        const { rowsForExport, encabezado, apiTotales, columnas } =
          await refrescarVentasPorHoraParaExport();
        if (!rowsForExport.length) {
          setWarning('No hay datos para generar este reporte.');
          return;
        }
        const fileName = `${reporte.endpoint}-${filters.fechaInicio}-${filters.fechaFin}.xlsx`;
        downloadVentasPorHoraExcelFile(
          rowsForExport,
          {
            fechaInicio: form.fechaInicio,
            fechaFin: form.fechaFin,
            cajaInicio: form.cajaInicio,
            cajaFin: form.cajaFin,
            turnoInicio: form.turnoInicio,
            turnoFin: form.turnoFin,
            encabezado,
            encabezadoFallback: popularidadEncabezadoFallback,
            sucursalNombre: selectedSuc?.nombreSucursal ?? '',
            reporteTitulo: VENTAS_POR_HORA_TITULO_DEFAULT,
            apiTotales,
            columnas,
          },
          fileName,
          excelSheetName(reporte.title)
        );
      } catch (e) {
        setError(e?.message || 'No se pudo generar el Excel.');
      } finally {
        setTablaReporteExportBusy(false);
      }
      return;
    }

    if (tipo === 'ingreso-por-dia' || ep === 'ingresos') {
      setError('');
      setWarning('');
      setTablaReporteExportBusy(true);
      try {
        const { rowsForExport, encabezado, apiTotales, columnas } =
          await refrescarIngresoPorDiaParaExport();
        if (!rowsForExport.length) {
          setWarning('No hay datos para generar este reporte.');
          return;
        }
        const xlsxBase = form.vistaIngreso === 'total' ? 'ingresos-total' : 'ingresos-por-dia';
        const fileName = `${xlsxBase}-${filters.fechaInicio}-${filters.fechaFin}.xlsx`;
        downloadIngresoPorDiaExcelFile(
          rowsForExport,
          {
            encabezadoApi: encabezado,
            encabezadoFallback: popularidadEncabezadoFallback,
            reporteTitulo: 'INGRESOS',
            apiTotales,
            columnas,
            vistaIngreso: form.vistaIngreso,
          },
          fileName,
          excelSheetName(reporte.title)
        );
      } catch (e) {
        setError(e?.message || 'No se pudo generar el Excel.');
      } finally {
        setTablaReporteExportBusy(false);
      }
      return;
    }

    const dataset = rows;
    if (!Array.isArray(dataset) || dataset.length === 0) {
      setWarning('No hay datos para generar este reporte.');
      return;
    }
    setWarning('');
    const flatRows = dataset.map((row) => {
      if (!row || typeof row !== 'object') return { valor: String(row ?? '') };
      const out = {};
      Object.entries(row).forEach(([k, v]) => {
        out[k] =
          v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : v;
      });
      return out;
    });
    const ws = XLSX.utils.json_to_sheet(flatRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
    const fileName = `${reporte.endpoint}-${filters.fechaInicio}-${filters.fechaFin}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const MSG_SIN_DATOS_EXPORT = 'No hay datos para generar este reporte.';
  const MSG_EXPORT_404_CON_FILAS =
    'No se pudo generar el archivo. Pulse «Ver reporte» para sincronizar filtros y tabla con lo que ve en pantalla; si ya lo hizo y sigue igual, el servidor puede no tener disponible esta exportación.';

  const ejecutarPdfSoloEnCliente = () => {
    const ep = reporte.endpoint;
    if (ep === 'descuentos-cortesias') {
      exportarPdfDescuentosCortesias();
      return;
    }
    if (ep === 'popularidad') {
      exportarPdfPopularidad();
      return;
    }
    if (ep === 'report-products-sold-by-waiter') {
      exportarPdfMeseroVista();
      return;
    }
    if (ep === 'cancelaciones') {
      exportarPdfCancelaciones();
      return;
    }
    if (ep === 'gastos') {
      exportarPdfGastos();
      return;
    }
    if (ep === 'relacion-ventas') {
      if (form.soloTotales) exportarPdfRelacionVentasSoloTotales();
      else exportarPdfRelacionVentas();
      return;
    }
    if (ep === 'ventas-por-hora') {
      exportarPdfVentasPorHora();
      return;
    }
    if (ep === 'ingresos') {
      exportarPdfIngresoPorDia();
    }
  };

  const exportar = async (formato) => {
    if (!reporte) return;
    const fmt = normalizeExportFormat(formato);
    const ep = reporte.endpoint;

    if (fmt === 'excel' || fmt === 'xlsx') {
      setError('');
      setWarning('');
      try {
        await exportarExcelLocal();
      } catch (err) {
        setError(err?.message || 'No se pudo generar el Excel en este equipo.');
      }
      return;
    }

    if (fmt === 'pdf' && EXPORT_PDF_EXCEL_SIN_API.has(ep)) {
      setError('');
      setWarning('');
      ejecutarPdfSoloEnCliente();
      return;
    }

    try {
      setError('');
      setWarning('');

      /** Red de seguridad: PDF/Excel nunca van al servidor para reportes de esta pantalla. */
      if (EXPORT_PDF_EXCEL_SIN_API.has(ep) && (fmt === 'pdf' || fmt === 'excel' || fmt === 'xlsx')) {
        if (fmt === 'excel' || fmt === 'xlsx') {
          try {
            await exportarExcelLocal();
          } catch (err) {
            setError(err?.message || 'No se pudo generar el Excel en este equipo.');
          }
          return;
        }
        ejecutarPdfSoloEnCliente();
        return;
      }

      const res =
        tipo === 'cancelaciones'
          ? await api.reportes.cancelacionesExport({ ...filters, formato: fmt })
          : await api.reportes.exportar(reporte.endpoint, fmt, filters);
      if (!res.ok) {
        if (res.status === 404 || res.status === 204) {
          setError('');
          const tieneFilas = Array.isArray(rows) && rows.length > 0;
          setWarning(tieneFilas ? MSG_EXPORT_404_CON_FILAS : MSG_SIN_DATOS_EXPORT);
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const extension = fmt === 'pdf' ? 'pdf' : 'xlsx';
      const finalExtension = fmt === 'txt' ? 'txt' : extension;
      const fileName = `${reporte.endpoint}-${filters.fechaInicio}-${filters.fechaFin}.${finalExtension}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'No se pudo exportar');
    }
  };

  if (!reporte) {
    return <div className="reportes-error">Reporte no encontrado.</div>;
  }

  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
  const showSucursal = sucursales.length > 0;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const setRelacionVistaMode = (mode) => {
    setForm((prev) => ({
      ...prev,
      soloTotales: mode === 'soloTotales',
      agruparTurno: mode === 'agruparTurno',
      ordenarMesa: mode === 'ordenarMesa',
    }));
  };

  const onRelacionVistaToggle = (mode, checked) => {
    if (checked) setRelacionVistaMode(mode);
    else setRelacionVistaMode(null);
  };

  const onPopularidadModeChange = (mode) => {
    if (mode === 'familia') {
      setForm((prev) => ({
        ...prev,
        porFamilia: true,
        mayorIngreso: false,
        mayorVenta: false,
        global: false,
      }));
      return;
    }
    if (mode === 'ingreso') {
      setForm((prev) => ({
        ...prev,
        porFamilia: false,
        mayorIngreso: true,
        mayorVenta: false,
        global: false,
      }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      porFamilia: false,
      mayorIngreso: false,
      mayorVenta: true,
      global: false,
    }));
  };

  const onMeseroAgrupacionPrincipalChange = (mode) => {
    if (mode === 'familias') {
      setForm((prev) => ({
        ...prev,
        porFamilia: true,
        mayorIngreso: false,
        mayorVenta: false,
        global: true,
        noGlobalAsterisco: false,
      }));
      return;
    }
    if (mode === 'ingreso') {
      setForm((prev) => ({
        ...prev,
        porFamilia: false,
        global: false,
        noGlobalAsterisco: false,
        mayorIngreso: true,
        mayorVenta: false,
      }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      porFamilia: false,
      global: false,
      noGlobalAsterisco: false,
      mayorIngreso: false,
      mayorVenta: true,
    }));
  };

  /** Familias: nivel Global vs Detallado; si Detallado, elegir Ejecutivo o Plano (noGlobalAsterisco). */
  const onMeseroFamiliasNivelChange = (nivel) => {
    setForm((prev) => ({
      ...prev,
      porFamilia: true,
      mayorIngreso: false,
      mayorVenta: false,
      global: nivel === 'global',
      noGlobalAsterisco: nivel === 'global' ? false : prev.noGlobalAsterisco,
    }));
  };

  const onMeseroFamiliasDetalleTipoChange = (tipo) => {
    setForm((prev) => ({
      ...prev,
      porFamilia: true,
      mayorIngreso: false,
      mayorVenta: false,
      global: false,
      noGlobalAsterisco: tipo === 'plano', // ejecutivo = tabla por apartado; plano = tabla plana 12 cols
    }));
  };

  const exportTablaRepideFetch =
    tipo === 'popularidad' ||
    tipo === 'descuentos-cortesias' ||
    tipo === 'cancelaciones' ||
    tipo === 'gastos' ||
    tipo === 'relacion-ventas' ||
    tipo === 'ventas-por-hora' ||
    tipo === 'ingreso-por-dia' ||
    tipo === 'productos-vendidos-por-mesero';

  return (
    <section className="reportes-page">
      <div className="reportes-head">
        <div className="reportes-title-box">
          <div className="reportes-label-container">
            <span className="reportes-label-group">
              <span className="reportes-label-prefix">Empresa</span>
              <span className="reportes-context-empresa">{empresaNombre || 'Cargando...'}</span>
            </span>
          </div>
          {sucursales.length > 0 && (
            <div className="reportes-head-filtros">
              <ReporteFiltrosEncabezadoLista
                className="reporte-filtros-meta--page"
                label="Sucursales"
                texto={filtrosEncabezado.sucursales.texto}
                items={filtrosEncabezado.sucursales.items}
              />
              <ReporteFiltrosEncabezadoLista
                className="reporte-filtros-meta--page"
                label="Cajas"
                texto={filtrosEncabezado.cajas.texto}
                items={filtrosEncabezado.cajas.items}
              />
            </div>
          )}
          <h1>
            {reporte.title}
            {tipo === 'productos-vendidos-por-mesero' && meseroEsModoFamilias() && (
              <span className="reportes-title-tag">
                Familias ·{' '}
                {form.global
                  ? 'Global'
                  : form.noGlobalAsterisco
                    ? 'Detallado · Plano'
                    : 'Detallado · Ejecutivo'}
              </span>
            )}
          </h1>
        </div>
      </div>

      <div className="reportes-panel">
        <div className="reportes-form-grid">
          {showSucursal && (
            <div className="reportes-section field-full">
              <h3 className="reportes-section-title">Filtros de cajas y sucursales</h3>
              <p className="reportes-section-hint">
                Marca &quot;Todas&quot; en sucursales para incluirlas todas. Las cajas se muestran
                por número (Caja 1, Caja 2…): al marcar una, se incluye en todas las sucursales
                seleccionadas que tengan ese número.
              </p>
              <ReportesFiltrosCajasEmpresa
                sucursales={sucursales}
                sucursalesTodas={form.sucursalesTodas}
                sucursalesSeleccionadas={form.sucursalesSeleccionadas}
                onSucursalesTodasChange={onSucursalesTodasChange}
                onSucursalCheckedChange={onSucursalCheckedChange}
                cajasTodas={form.cajasTodas}
                permitirCajasTodas={permitirCajasTodas}
                cajasSeleccionadas={form.cajasSeleccionadas}
                onCajasTodasChange={onCajasTodasChange}
                onCajaCheckedChange={onCajaCheckedChange}
                cajasCatalogo={cajasCatalogoList}
                loading={cajasCatalogoLoading}
              />
            </div>
          )}

          {tipo === 'productos-vendidos-por-mesero' && (
            <div className="reportes-section field-full">
              <h3 className="reportes-section-title">Mesero y cortesías</h3>
              <p className="reportes-section-hint">
                Opciones de filtrado. Deja el mesero vacío para ver todos.
              </p>
              <div className="reportes-option-grid">
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.soloCortesias}
                    onChange={(e) => setField('soloCortesias', e.target.checked)}
                  />
                  <span className="reportes-option-text">Solo cortesías</span>
                </label>
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.incluirCortesias}
                    onChange={(e) => setField('incluirCortesias', e.target.checked)}
                  />
                  <span className="reportes-option-text">Incluir cortesías</span>
                </label>
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.soloUsanAutorizacion}
                    onChange={(e) => setField('soloUsanAutorizacion', e.target.checked)}
                  />
                  <span className="reportes-option-text">Solo con autorización</span>
                </label>
              </div>
              <label className="field field-full reportes-field-spaced">
                <span>Mesero (ID)</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.meseroId}
                  onChange={(e) => setField('meseroId', e.target.value)}
                  placeholder="Ej. 5 — vacío = todos los meseros"
                  autoComplete="off"
                />
              </label>
            </div>
          )}

          <div className="reportes-section field-full">
            <h3 className="reportes-section-title">
              {tipo === 'gastos' ? 'Reporte de gastos' : 'Periodo y turnos'}
            </h3>
            <p className="reportes-section-hint">
              {tipo === 'gastos'
                ? 'Fechas, rango de cajas, turnos y vista global como en SmartNet legacy.'
                : 'Rango de fechas, caja y turno incluidos en el reporte.'}
            </p>
            <p className="reportes-section-hint reportes-caja-hint">
              {cajasTodasEfectivas
                ? 'Todas las cajas incluidas.'
                : form.cajasSeleccionadas.length > 0
                  ? `${form.cajasSeleccionadas.length} caja(s) seleccionada(s).`
                  : !form.sucursalesTodas && form.sucursalesSeleccionadas.length > 0
                    ? 'Marca TODAS las cajas o al menos una caja de las sucursales elegidas.'
                    : 'Sin filtro de caja (equivalente a todas).'}
            </p>
            <p className="reportes-section-hint">
              Periodo seleccionado: {isoADisplayDDMMAAAA(form.fechaInicio)} - {isoADisplayDDMMAAAA(form.fechaFin)}
            </p>
            {tipo === 'gastos' ? (
              <div className="reportes-gastos-filtros">
                <div className="reportes-inner-grid reportes-gastos-filtros__grid">
                  <label className="field">
                    <span>Fecha comienzo</span>
                    <input
                      type="date"
                      lang="es-MX"
                      value={form.fechaInicio}
                      onChange={(e) => setField('fechaInicio', e.target.value)}
                    />
                  </label>
                  <label className="field">
                    <span>Fecha final</span>
                    <input
                      type="date"
                      lang="es-MX"
                      value={form.fechaFin}
                      onChange={(e) => setField('fechaFin', e.target.value)}
                    />
                  </label>
                  <label className="check-field reportes-option reportes-gastos-global">
                    <input
                      type="checkbox"
                      checked={form.gastosGlobal}
                      onChange={(e) => setField('gastosGlobal', e.target.checked)}
                    />
                    <span className="reportes-option-text">Vista global</span>
                  </label>
                  <p className="reportes-section-hint reportes-gastos-vista-hint">
                    {form.gastosGlobal
                      ? 'Solo totales por tipo de gasto (sin desglose de conceptos).'
                      : 'Detallado: cada gasto con fecha, folio y concepto agrupado por tipo.'}
                  </p>
                </div>
                <div className="reportes-gastos-pick-row">
                  <label className="field reportes-gastos-pick-row__code">
                    <span>Turno comienzo</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={displayNumericField(form.turnoInicio)}
                      onChange={(e) => {
                        const next = parseNumericFieldInput(e.target.value);
                        if (next === undefined) return;
                        setForm((prev) => {
                          const { inicio, fin } = applyGastosRangoInicio(next, prev.turnoFin);
                          return { ...prev, turnoInicio: inicio, turnoFin: fin };
                        });
                      }}
                    />
                  </label>
                  <div className="reportes-gastos-pick-row__name">
                    {gastosCatalogo.turnoInicioNombre || getTurnoNombreDisplay(form.turnoInicio) || '—'}
                  </div>
                </div>
                <div className="reportes-gastos-pick-row">
                  <label className="field reportes-gastos-pick-row__code">
                    <span>Turno final</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={displayNumericField(form.turnoFin)}
                      onChange={(e) => {
                        const next = parseNumericFieldInput(e.target.value);
                        if (next === undefined) return;
                        if (!acceptGastosRangoFin(form.turnoInicio, next)) return;
                        setField('turnoFin', next);
                      }}
                    />
                  </label>
                  <div
                    className={`reportes-gastos-pick-row__name${
                      gastosTurnoRangoInvalido ? ' is-invalid' : ''
                    }`}
                  >
                    {gastosTurnoRangoInvalido
                      ? 'Debe ser ≥ turno comienzo'
                      : gastosCatalogo.turnoFinalNombre ||
                        getTurnoNombreDisplay(form.turnoFin) ||
                        '—'}
                  </div>
                </div>
              </div>
            ) : (
            <>
            <div className="reportes-inner-grid">
              <label className="field">
                <span>Fecha comienzo</span>
                <input type="date" lang="es-MX" value={form.fechaInicio} onChange={(e) => setField('fechaInicio', e.target.value)} />
              </label>
              <label className="field">
                <span>Fecha final</span>
                <input type="date" lang="es-MX" value={form.fechaFin} onChange={(e) => setField('fechaFin', e.target.value)} />
              </label>
              <label className="field">
                <span>Turno comienzo</span>
                <input
                  type="number"
                  min={0}
                  value={form.turnoInicio}
                  onChange={(e) => setField('turnoInicio', Number(e.target.value || 0))}
                />
              </label>
              <label className="field">
                <span>Turno final</span>
                <input
                  type="number"
                  min={0}
                  value={form.turnoFin}
                  onChange={(e) => setField('turnoFin', Number(e.target.value || 0))}
                />
              </label>
            </div>
            </>
            )}
          </div>

          {tipo === 'descuentos-cortesias' && (
            <div className="reportes-section field-full">
              <h3 className="reportes-section-title">Orden</h3>
              <div className="reportes-option-grid reportes-option-grid--auto">
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.ordenarMesa}
                    onChange={(e) => setField('ordenarMesa', e.target.checked)}
                  />
                  <span className="reportes-option-text">Ordenar por cuenta</span>
                </label>
              </div>
            </div>
          )}

          {tipo === 'cancelaciones' && (
            <div className="reportes-section field-full">
              <h3 className="reportes-section-title">Detalle</h3>
              <div className="reportes-option-grid reportes-option-grid--auto">
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.incluirEstadisticas}
                    onChange={(e) => setField('incluirEstadisticas', e.target.checked)}
                  />
                  <span className="reportes-option-text">Incluir estadísticas</span>
                </label>
              </div>
            </div>
          )}

          {tipo === 'relacion-ventas' && (
            <div className="reportes-section field-full">
              <h3 className="reportes-section-title">Vista de relación</h3>
              <p className="reportes-section-hint">
                Opcional. Sin selección se muestra la vista detallada por defecto.
              </p>
              <div className="reportes-option-grid">
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.soloTotales}
                    onChange={(e) => onRelacionVistaToggle('soloTotales', e.target.checked)}
                  />
                  <span className="reportes-option-text">Solo totales finales</span>
                </label>
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.agruparTurno}
                    onChange={(e) => onRelacionVistaToggle('agruparTurno', e.target.checked)}
                  />
                  <span className="reportes-option-text">Agrupar por fecha y turno</span>
                </label>
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.ordenarMesa}
                    onChange={(e) => onRelacionVistaToggle('ordenarMesa', e.target.checked)}
                  />
                  <span className="reportes-option-text">Orden por mesa</span>
                </label>
              </div>
            </div>
          )}

          {tipo === 'ingreso-por-dia' && (
            <div className="reportes-section field-full">
              <h3 className="reportes-section-title">Vista de ingresos</h3>
              <p className="reportes-section-hint">Elige cómo quieres ver los datos.</p>
              <div className="reportes-option-grid" role="radiogroup" aria-label="Vista de ingresos">
                <label className="check-field reportes-option">
                  <input
                    type="radio"
                    name="vista-ingreso"
                    value="dia"
                    checked={form.vistaIngreso === 'dia'}
                    onChange={() => setField('vistaIngreso', 'dia')}
                  />
                  <span className="reportes-option-text">Por día</span>
                </label>
                <label className="check-field reportes-option">
                  <input
                    type="radio"
                    name="vista-ingreso"
                    value="total"
                    checked={form.vistaIngreso === 'total'}
                    onChange={() => setField('vistaIngreso', 'total')}
                  />
                  <span className="reportes-option-text">Total</span>
                </label>
              </div>
            </div>
          )}

          {tipo === 'popularidad' && (
            <div className="reportes-section field-full">
              <h3 className="reportes-section-title">Productos y categorías</h3>
              <p className="reportes-section-hint">Qué incluir en el análisis de popularidad.</p>
              <div className="reportes-option-grid">
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.soloCortesias}
                    onChange={(e) => setField('soloCortesias', e.target.checked)}
                  />
                  <span className="reportes-option-text">Solo cortesías</span>
                </label>
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.incluirCortesias}
                    onChange={(e) => setField('incluirCortesias', e.target.checked)}
                  />
                  <span className="reportes-option-text">Incluir cortesías</span>
                </label>
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.bebidasAlcoholicas}
                    onChange={(e) => setField('bebidasAlcoholicas', e.target.checked)}
                  />
                  <span className="reportes-option-text">Solo bebidas alcohólicas</span>
                </label>
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.incluirAlimentos}
                    onChange={(e) => setField('incluirAlimentos', e.target.checked)}
                  />
                  <span className="reportes-option-text">Incluir alimentos</span>
                </label>
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.incluirBebidas}
                    onChange={(e) => setField('incluirBebidas', e.target.checked)}
                  />
                  <span className="reportes-option-text">Incluir bebidas</span>
                </label>
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.incluirOtros}
                    onChange={(e) => setField('incluirOtros', e.target.checked)}
                  />
                  <span className="reportes-option-text">Incluir otros</span>
                </label>
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.incluirPrecioMenu}
                    onChange={(e) => setField('incluirPrecioMenu', e.target.checked)}
                  />
                  <span className="reportes-option-text">Incluir precio menú</span>
                </label>
              </div>
              <div className="reportes-subsection">
                <h4 className="reportes-subsection-title">Cómo ordenar resultados</h4>
                <p className="reportes-section-hint">
                  Solo aplica un criterio. En la <strong>tabla de pantalla</strong>, las filas pasan a
                  ordenarse de <strong>mayor a menor</strong>: por importe/total (mayor ingreso) o por
                  cantidad/volumen (mayor venta). Si el API ya ordena distinto, la vista prioriza este
                  criterio. Excel/PDF del servidor pueden seguir el orden del backend.
                </p>
                <div className="reportes-radio-row" role="radiogroup" aria-label="Orden popularidad">
                  <label
                    className={`reportes-radio-pill${form.porFamilia && !form.mayorIngreso && !form.mayorVenta ? ' is-selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="agrup-popularidad-orden"
                      checked={form.porFamilia && !form.mayorIngreso && !form.mayorVenta}
                      onChange={() => onPopularidadModeChange('familia')}
                    />
                    <span>Por familia</span>
                  </label>
                  <label className={`reportes-radio-pill${form.mayorIngreso ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="agrup-popularidad-orden"
                      checked={form.mayorIngreso}
                      onChange={() => onPopularidadModeChange('ingreso')}
                    />
                    <span>Mayor ingreso</span>
                  </label>
                  <label className={`reportes-radio-pill${form.mayorVenta ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="agrup-popularidad-orden"
                      checked={form.mayorVenta}
                      onChange={() => onPopularidadModeChange('venta')}
                    />
                    <span>Mayor venta</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {tipo === 'productos-vendidos-por-mesero' && (
            <div className="reportes-section field-full">
              <h3 className="reportes-section-title">Contenido del reporte</h3>
              <p className="reportes-section-hint">Tipos de producto y presentación en columnas.</p>
              <div className="reportes-option-grid">
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.incluirAlimentos}
                    onChange={(e) => setField('incluirAlimentos', e.target.checked)}
                  />
                  <span className="reportes-option-text">Incluir alimentos</span>
                </label>
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.incluirBebidas}
                    onChange={(e) => setField('incluirBebidas', e.target.checked)}
                  />
                  <span className="reportes-option-text">Incluir bebidas</span>
                </label>
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.incluirOtros}
                    onChange={(e) => setField('incluirOtros', e.target.checked)}
                  />
                  <span className="reportes-option-text">Incluir otros</span>
                </label>
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.incluirPrecioMenu}
                    onChange={(e) => setField('incluirPrecioMenu', e.target.checked)}
                  />
                  <span className="reportes-option-text">Incluir precio menú</span>
                </label>
                <label className="check-field reportes-option">
                  <input
                    type="checkbox"
                    checked={form.mostrarDescripcion}
                    onChange={(e) => setField('mostrarDescripcion', e.target.checked)}
                  />
                  <span className="reportes-option-text">Mostrar descripción</span>
                </label>
              </div>
              <fieldset className="reportes-fieldset field-full">
                <legend className="reportes-fieldset-legend">Agrupación de resultados</legend>
                <p className="reportes-fieldset-hint">
                  Elige el criterio principal. Si seleccionas <strong>Familias</strong>, primero define{' '}
                  <strong>Global</strong> (solo totales por familia) o <strong>Detallado</strong>; si es detallado, elige{' '}
                  <strong>Ejecutivo</strong> o <strong>Plano</strong>. Por defecto: Global.{' '}
                  <strong>Mayor ingreso</strong> y <strong>mayor venta</strong> ordenan productos (y bloques de familia) de mayor a menor por importe o por cantidad en la vista y en el PDF generado aquí.
                </p>
                <div className="reportes-radio-row" role="radiogroup" aria-label="Criterio de agrupación">
                  <label
                    className={`reportes-radio-pill${meseroEsModoFamilias() ? ' is-selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="agrup-mesero-principal"
                      checked={meseroEsModoFamilias()}
                      onChange={() => onMeseroAgrupacionPrincipalChange('familias')}
                    />
                    <span>Familias</span>
                  </label>
                  <label className={`reportes-radio-pill${form.mayorIngreso ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="agrup-mesero-principal"
                      checked={form.mayorIngreso}
                      onChange={() => onMeseroAgrupacionPrincipalChange('ingreso')}
                    />
                    <span>Mayor ingreso</span>
                  </label>
                  <label className={`reportes-radio-pill${form.mayorVenta ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="agrup-mesero-principal"
                      checked={form.mayorVenta}
                      onChange={() => onMeseroAgrupacionPrincipalChange('venta')}
                    />
                    <span>Mayor venta</span>
                  </label>
                </div>

                {meseroEsModoFamilias() && (
                  <div className="reportes-sub-agrupacion">
                    <p className="reportes-sub-agrupacion-title">Global y detallado</p>
                    <p className="reportes-sub-agrupacion-hint">
                      Primero elige <strong>Global</strong> (solo totales por familia, opción por defecto) o{' '}
                      <strong>Detallado</strong> (productos por mesero). Si es detallado, elige{' '}
                      <strong>Ejecutivo</strong> (por apartado bebidas/alimentos y familias en tabla) o{' '}
                      <strong>Plano</strong> (tabla única de 12 columnas, mismo formato que Excel/PDF).
                    </p>
                    <div className="reportes-radio-row" role="radiogroup" aria-label="Global o detallado">
                      <label className={`reportes-radio-pill${form.global ? ' is-selected' : ''}`}>
                        <input
                          type="radio"
                          name="agrup-mesero-familias-nivel"
                          checked={form.global}
                          onChange={() => onMeseroFamiliasNivelChange('global')}
                        />
                        <span>Global</span>
                      </label>
                      <label className={`reportes-radio-pill${!form.global ? ' is-selected' : ''}`}>
                        <input
                          type="radio"
                          name="agrup-mesero-familias-nivel"
                          checked={!form.global}
                          onChange={() => onMeseroFamiliasNivelChange('detallado')}
                        />
                        <span>Detallado</span>
                      </label>
                    </div>
                    {!form.global && (
                      <div
                        className="reportes-sub-agrupacion-detalle"
                        role="radiogroup"
                        aria-label="Tipo de vista detallada"
                      >
                        <p className="reportes-sub-agrupacion-detalle-title">Tipo de detalle</p>
                        <div className="reportes-radio-row">
                          <label
                            className={`reportes-radio-pill${!form.global && !form.noGlobalAsterisco ? ' is-selected' : ''}`}
                          >
                            <input
                              type="radio"
                              name="agrup-mesero-familias-detalle-tipo"
                              checked={!form.global && !form.noGlobalAsterisco}
                              onChange={() => onMeseroFamiliasDetalleTipoChange('ejecutivo')}
                            />
                            <span>Ejecutivo</span>
                          </label>
                          <label
                            className={`reportes-radio-pill${!form.global && form.noGlobalAsterisco ? ' is-selected' : ''}`}
                          >
                            <input
                              type="radio"
                              name="agrup-mesero-familias-detalle-tipo"
                              checked={!form.global && form.noGlobalAsterisco}
                              onChange={() => onMeseroFamiliasDetalleTipoChange('plano')}
                            />
                            <span>Plano</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </fieldset>
              <fieldset className="reportes-fieldset field-full reportes-field-spaced">
                <legend className="reportes-fieldset-legend">Orden de meseros (vista, PDF y Excel)</legend>
                <p className="reportes-fieldset-hint">
                  Cómo se listan los bloques de cada mesero: por ventas totales, por su código (id) o por nombre.
                </p>
                <div className="reportes-radio-row" role="radiogroup" aria-label="Orden de meseros">
                  <label className={`reportes-radio-pill${form.ordenMeseros === 'ventas-desc' ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="orden-meseros"
                      checked={form.ordenMeseros === 'ventas-desc'}
                      onChange={() => setField('ordenMeseros', 'ventas-desc')}
                    />
                    <span>Mayor ventas</span>
                  </label>
                  <label className={`reportes-radio-pill${form.ordenMeseros === 'ventas-asc' ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="orden-meseros"
                      checked={form.ordenMeseros === 'ventas-asc'}
                      onChange={() => setField('ordenMeseros', 'ventas-asc')}
                    />
                    <span>Menor ventas</span>
                  </label>
                  <label className={`reportes-radio-pill${form.ordenMeseros === 'codigo-asc' ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="orden-meseros"
                      checked={form.ordenMeseros === 'codigo-asc'}
                      onChange={() => setField('ordenMeseros', 'codigo-asc')}
                    />
                    <span>Código (menor a mayor)</span>
                  </label>
                  <label className={`reportes-radio-pill${form.ordenMeseros === 'codigo-desc' ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="orden-meseros"
                      checked={form.ordenMeseros === 'codigo-desc'}
                      onChange={() => setField('ordenMeseros', 'codigo-desc')}
                    />
                    <span>Código (mayor a menor)</span>
                  </label>
                  <label className={`reportes-radio-pill${form.ordenMeseros === 'nombre-asc' ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="orden-meseros"
                      checked={form.ordenMeseros === 'nombre-asc'}
                      onChange={() => setField('ordenMeseros', 'nombre-asc')}
                    />
                    <span>Nombre A-Z</span>
                  </label>
                  <label className={`reportes-radio-pill${form.ordenMeseros === 'nombre-desc' ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="orden-meseros"
                      checked={form.ordenMeseros === 'nombre-desc'}
                      onChange={() => setField('ordenMeseros', 'nombre-desc')}
                    />
                    <span>Nombre Z-A</span>
                  </label>
                </div>
              </fieldset>
            </div>
          )}
        </div>

        <div className="reportes-actions">
          {reporteDesactualizado && (
            <p className="reportes-stale-hint" role="status">
              Cambiaste filtros. Pulsa <strong>Ver reporte</strong> para actualizar los datos.
            </p>
          )}
          <button
            type="button"
            className={`reportes-btn reportes-btn-primary${reporteDesactualizado ? ' reportes-btn-primary--pulse' : ''}`}
            onClick={loadReport}
          >
            Ver reporte
          </button>
          {tipo === 'productos-vendidos-por-mesero' ? (
            <button
              type="button"
              className="reportes-btn reportes-btn-secondary"
              disabled={tablaReporteExportBusy}
              onClick={() => exportarPdfMeseroVista()}
            >
              {tablaReporteExportBusy ? 'PDF (obteniendo datos…)' : 'PDF (formato reporte)'}
            </button>
          ) : tipo === 'popularidad' ? (
            <button
              type="button"
              className="reportes-btn reportes-btn-secondary"
              disabled={tablaReporteExportBusy}
              onClick={() => exportarPdfPopularidad()}
            >
              {tablaReporteExportBusy ? 'PDF (obteniendo datos…)' : 'PDF (formato reporte)'}
            </button>
          ) : tipo === 'descuentos-cortesias' ? (
            <button
              type="button"
              className="reportes-btn reportes-btn-secondary"
              disabled={tablaReporteExportBusy}
              onClick={() => exportarPdfDescuentosCortesias()}
            >
              {tablaReporteExportBusy ? 'PDF (obteniendo datos…)' : 'PDF (formato reporte)'}
            </button>
          ) : tipo === 'cancelaciones' ? (
            <button
              type="button"
              className="reportes-btn reportes-btn-secondary"
              disabled={tablaReporteExportBusy}
              onClick={() => exportarPdfCancelaciones()}
            >
              {tablaReporteExportBusy ? 'PDF (obteniendo datos…)' : 'PDF (formato reporte)'}
            </button>
          ) : tipo === 'gastos' ? (
            <button
              type="button"
              className="reportes-btn reportes-btn-secondary"
              disabled={tablaReporteExportBusy}
              onClick={() => exportarPdfGastos()}
            >
              {tablaReporteExportBusy ? 'PDF (obteniendo datos…)' : 'PDF (formato reporte)'}
            </button>
          ) : tipo === 'relacion-ventas' ? (
            form.soloTotales ? (
              <button
                type="button"
                className="reportes-btn reportes-btn-secondary"
                disabled={tablaReporteExportBusy}
                onClick={() => exportarPdfRelacionVentasSoloTotales()}
              >
                {tablaReporteExportBusy ? 'PDF (obteniendo datos…)' : 'PDF (solo totales)'}
              </button>
            ) : (
              <button
                type="button"
                className="reportes-btn reportes-btn-secondary"
                disabled={tablaReporteExportBusy}
                onClick={() => exportarPdfRelacionVentas()}
              >
                {tablaReporteExportBusy ? 'PDF (obteniendo datos…)' : 'PDF (formato reporte)'}
              </button>
            )
          ) : tipo === 'ventas-por-hora' ? (
            <button
              type="button"
              className="reportes-btn reportes-btn-secondary"
              disabled={tablaReporteExportBusy}
              onClick={() => exportarPdfVentasPorHora()}
            >
              {tablaReporteExportBusy ? 'PDF (obteniendo datos…)' : 'PDF (formato reporte)'}
            </button>
          ) : tipo === 'ingreso-por-dia' ? (
            <button
              type="button"
              className="reportes-btn reportes-btn-secondary"
              disabled={tablaReporteExportBusy}
              onClick={() => exportarPdfIngresoPorDia()}
            >
              {tablaReporteExportBusy ? 'PDF (obteniendo datos…)' : 'PDF (formato reporte)'}
            </button>
          ) : (
            <button type="button" className="reportes-btn reportes-btn-secondary" onClick={() => exportar('pdf')}>
              PDF
            </button>
          )}
          <button
            type="button"
            className="reportes-btn reportes-btn-secondary"
            disabled={exportTablaRepideFetch && tablaReporteExportBusy}
            onClick={() => exportar('excel')}
          >
            {exportTablaRepideFetch && tablaReporteExportBusy ? 'Excel (obteniendo datos…)' : 'Excel'}
          </button>
        </div>
      </div>

      {loading && <div className="reportes-loading">Cargando reporte...</div>}
      {exportTablaRepideFetch && tablaReporteExportBusy && !loading && (
        <div className="reportes-loading">Obteniendo datos del servidor para PDF o Excel…</div>
      )}
      {!!error && <div className="reportes-error">Error: {error}</div>}
      {!!warning && <div className="reportes-warning">{warning}</div>}

      {tipo === 'productos-vendidos-por-mesero' && (
        <div className="reportes-mesero-vista">
          <div className="reportes-mesero-vista-head">
            <div className="reportes-mesero-vista-intro">
              <h2 className="reportes-mesero-vista-title">Vista del reporte</h2>
              <p className="reportes-mesero-vista-sub">
                {meseroGlobalSoloTotalesFamilia ? (
                  <>
                    Vista <span className="reportes-mesero-vista-em">global</span>: solo{' '}
                    <span className="reportes-mesero-vista-em">familias</span> y totales, sin detalle de
                    menú/productos (igual que el PDF descargable).
                  </>
                ) : form.noGlobalAsterisco ? (
                  <>
                    Vista <span className="reportes-mesero-vista-em">plano</span>: una tabla de 12 columnas con
                    todas las líneas (misma forma que Excel/PDF).
                  </>
                ) : (
                  <>
                    Vista <span className="reportes-mesero-vista-em">ejecutiva</span>: por mesero, apartados y
                    familias en tabla (misma forma que Excel/PDF).
                  </>
                )}
              </p>
            </div>
            <div
              className={`reportes-mesero-vista-status${
                loading ? ' is-loading' : ''
              }${!loading && error ? ' is-error' : ''}${
                !loading && !error && rows.length === 0 ? ' is-empty' : ''
              }${!loading && !error && rows.length > 0 ? ' is-ok' : ''}`}
              aria-live="polite"
            >
              {loading && 'Actualizando…'}
              {!loading && error && 'Error al cargar'}
              {!loading && !error && rows.length === 0 && 'Sin filas en este rango'}
              {!loading && !error && rows.length > 0 && `${rows.length} líneas`}
            </div>
          </div>

          <div className="reportes-preview-report-shell">
            <ProductosMeseroReportLayout
              className="pm-report--embedded"
              rows={rows}
              encabezado={encabezadoMesero}
              encabezadoFallback={popularidadEncabezadoFallback}
              sucursalNombre={
              filtrosEncabezado.sucursales.items.length === 1
                ? filtrosEncabezado.sucursales.items[0]
                : ''
            }
              filtrosTexto={popularidadEncabezadoFallback.filtrosTexto}
              fechaInicio={form.fechaInicio}
              fechaFin={form.fechaFin}
              cajaInicio={form.cajaInicio}
              cajaFin={form.cajaFin}
              turnoInicio={form.turnoInicio}
              turnoFin={form.turnoFin}
              globalSoloTotalesPorFamilia={meseroGlobalSoloTotalesFamilia}
              noGlobalAsterisco={form.noGlobalAsterisco}
              mayorIngreso={form.mayorIngreso}
              mayorVenta={form.mayorVenta}
              ordenMeseros={form.ordenMeseros}
            />
          </div>

        </div>
      )}

      {!loading && !error && tipo === 'popularidad' && (
        <div className="reportes-popularidad-vista">
          <div className="reportes-mesero-vista-head">
            <h2 className="reportes-mesero-vista-title">Vista del reporte</h2>
          </div>
          <PopularidadReportLayout
            className="pop-report--embedded"
            rawRows={tablaRows}
            fechaInicio={form.fechaInicio}
            fechaFin={form.fechaFin}
            cajaInicio={form.cajaInicio}
            cajaFin={form.cajaFin}
            turnoInicio={form.turnoInicio}
            turnoFin={form.turnoFin}
            encabezadoApi={encabezadoPopularidad}
            encabezadoFallback={popularidadEncabezadoFallback}
            sucursalNombre={
              filtrosEncabezado.sucursales.items.length === 1
                ? filtrosEncabezado.sucursales.items[0]
                : ''
            }
            reporteTitulo="REPORTE POPULARIDAD DE PRODUCTOS"
            incluirCortesias={form.incluirCortesias}
            footer={popularidadFooter}
            bebidasAlcoholicas={form.bebidasAlcoholicas}
            incluirPrecioMenu={form.incluirPrecioMenu}
          />
        </div>
      )}

      {!loading && !error && tipo === 'descuentos-cortesias' && (
        <div className="reportes-preview-report-shell">
          <DescuentosCortesiasReportLayout
            className="dc-report--embedded"
            rawRows={tablaRows}
            fechaInicio={form.fechaInicio}
            fechaFin={form.fechaFin}
            cajaInicio={form.cajaInicio}
            cajaFin={form.cajaFin}
            turnoInicio={form.turnoInicio}
            turnoFin={form.turnoFin}
            encabezadoApi={encabezadoDescuentos}
            encabezadoFallback={popularidadEncabezadoFallback}
            sucursalNombre={
              filtrosEncabezado.sucursales.items.length === 1
                ? filtrosEncabezado.sucursales.items[0]
                : ''
            }
            reporteTitulo="RELACIÓN DE DESCUENTOS Y CORTESÍAS"
            apiTotales={descuentosApiBundle?.totales ?? null}
            columnasLabels={descuentosApiBundle?.columnas ?? null}
          />
        </div>
      )}

      {!loading && !error && tipo === 'cancelaciones' && (
        <div className="reportes-preview-report-shell">
          <CancelacionesReportLayout
            className="dc-report--embedded"
            rawRows={tablaRows}
            fechaInicio={form.fechaInicio}
            fechaFin={form.fechaFin}
            cajaInicio={form.cajaInicio}
            cajaFin={form.cajaFin}
            turnoInicio={form.turnoInicio}
            turnoFin={form.turnoFin}
            encabezadoApi={encabezadoCancelaciones}
            encabezadoFallback={popularidadEncabezadoFallback}
            sucursalNombre={
              filtrosEncabezado.sucursales.items.length === 1
                ? filtrosEncabezado.sucursales.items[0]
                : ''
            }
            reporteTitulo={CANCELACIONES_REPORT_TITULO_DEFAULT}
            apiTotales={cancelacionesApiBundle?.totales ?? null}
            columnasLabels={cancelacionesApiBundle?.columnas ?? null}
          />
        </div>
      )}

      {!loading && !error && tipo === 'gastos' && (
        <div className="reportes-preview-report-shell">
          <GastosReportLayout
            className="dc-report--embedded"
            rawRows={tablaRows}
            fechaInicio={form.fechaInicio}
            fechaFin={form.fechaFin}
            cajaInicio={form.cajaInicio}
            cajaFin={form.cajaFin}
            turnoInicio={form.turnoInicio}
            turnoFin={form.turnoFin}
            encabezadoApi={encabezadoGastos}
            encabezadoFallback={popularidadEncabezadoFallback}
            sucursalNombre={
              filtrosEncabezado.sucursales.items.length === 1
                ? filtrosEncabezado.sucursales.items[0]
                : ''
            }
            reporteTitulo={GASTOS_REPORT_TITULO_DEFAULT}
            apiTotales={gastosApiBundle?.totales ?? null}
            columnasLabels={gastosApiBundle?.columnas ?? null}
            globalView={form.gastosGlobal}
          />
        </div>
      )}

      {!loading && !error && tipo === 'relacion-ventas' && (
        <div className="reportes-preview-report-shell">
          {form.soloTotales ? (
            <RelacionVentasSoloTotalesLayout
              resumen={relacionVentasSoloTotales}
              encabezadoApi={encabezadoRelacionVentas}
              encabezadoFallback={popularidadEncabezadoFallback}
              ocultarFolios={soloTotalesOcultarFolios}
              sucursalNombre={
                filtrosEncabezado.sucursales.items.length === 1
                  ? filtrosEncabezado.sucursales.items[0]
                  : ''
              }
              className="dc-report--embedded"
            />
          ) : (
            <RelacionVentasReportLayout
              className="dc-report--embedded"
              rawRows={tablaRows}
              fechaInicio={form.fechaInicio}
              fechaFin={form.fechaFin}
              cajaInicio={form.cajaInicio}
              cajaFin={form.cajaFin}
              turnoInicio={form.turnoInicio}
              turnoFin={form.turnoFin}
              encabezadoApi={encabezadoRelacionVentas}
              encabezadoFallback={popularidadEncabezadoFallback}
              sucursalNombre={
                filtrosEncabezado.sucursales.items.length === 1
                  ? filtrosEncabezado.sucursales.items[0]
                  : ''
              }
              reporteTitulo={RELACION_VENTAS_TITULO_DEFAULT}
              apiTotales={relacionVentasApiBundle?.totales ?? null}
              columnasLabels={relacionVentasApiBundle?.columnas ?? null}
              ordenarMesa={form.ordenarMesa}
              agruparTurno={form.agruparTurno}
            />
          )}
        </div>
      )}

      {!loading && !error && tipo === 'ventas-por-hora' && (
        <div className="reportes-preview-report-shell">
          <VentasPorHoraReportLayout
            className="dc-report--embedded"
            rawRows={tablaRows}
            fechaInicio={form.fechaInicio}
            fechaFin={form.fechaFin}
            cajaInicio={form.cajaInicio}
            cajaFin={form.cajaFin}
            turnoInicio={form.turnoInicio}
            turnoFin={form.turnoFin}
            encabezadoApi={encabezadoVentasPorHora}
            encabezadoFallback={popularidadEncabezadoFallback}
            sucursalNombre={
              filtrosEncabezado.sucursales.items.length === 1
                ? filtrosEncabezado.sucursales.items[0]
                : ''
            }
            apiTotales={ventasPorHoraApiBundle?.totales ?? null}
            columnasLabels={ventasPorHoraApiBundle?.columnas ?? null}
          />
        </div>
      )}

      {!loading && !error && tipo === 'ingreso-por-dia' && (
        <div className="reportes-preview-report-shell">
          <IngresoPorDiaReportLayout
            className="dc-report--embedded"
            rawRows={tablaRows}
            encabezadoApi={encabezadoIngresoPorDia}
            encabezadoFallback={popularidadEncabezadoFallback}
            apiTotales={ingresoPorDiaApiBundle?.totales ?? null}
            columnasLabels={ingresoPorDiaApiBundle?.columnas ?? null}
            vistaIngreso={form.vistaIngreso}
          />
        </div>
      )}

      {!loading &&
        !error &&
        tipo !== 'productos-vendidos-por-mesero' &&
        tipo !== 'popularidad' &&
        tipo !== 'descuentos-cortesias' &&
        tipo !== 'cancelaciones' &&
        tipo !== 'gastos' &&
        tipo !== 'relacion-ventas' &&
        tipo !== 'ventas-por-hora' &&
        tipo !== 'ingreso-por-dia' && (
        <div className="reportes-table-wrap">
          <table className="reportes-table">
            <thead>
              <tr>
                {headers.map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={Math.max(headers.length, 1)} className="reportes-empty">
                    Sin resultados para el rango seleccionado.
                  </td>
                </tr>
              )}
              {tablaRows.map((row, idx) => (
                <tr key={idx}>
                  {headers.map((h) => (
                    <td key={`${idx}-${h}`}>{formatCell(row[h])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
